/* ==========================================================================
   Aura Smart. Shared runtime
   Chrome (header, menus, search, cart, footer), cart store, product cards,
   and the motion layer. Pages call A.ready(fn) for page-specific code.
   ========================================================================== */
(() => {
  const D = window.AURA;
  const route = D.route;
  const I = window.AURA_ICONS;
  const root = document.documentElement;
  root.classList.remove('no-js');
  root.classList.add('js');
  // ?noanim renders the final state instantly (QA screenshots); also honours reduced motion
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches || /[?&]noanim(?:&|=|$)/.test(location.search);
  if (reduced) root.classList.add('reduce');
  const fine = matchMedia('(hover: hover) and (pointer: fine)').matches;
  const touch = matchMedia('(hover: none)').matches;   // the same test the touch styles in main.css use

  /* ---------- a screen height that holds still ----------
     On a phone the address bar slides in and out as you scroll, and many browsers (in-app
     browsers especially) recompute viewport units each time. Everything sized from them, the
     hero, the pinned story, the banner and the footer, would change height mid-scroll: the
     hero photo re-crops, its clouds canvas is wiped, and the page below jumps. So touch screens
     measure the screen once and keep it until the width changes (the phone is turned); the
     keyboard opening doesn't count either. main.css sizes those sections from --screen.
     --screen-full is the whole screen, for scenes that fill it (blackout-blinds.html): Safari on
     iPhone draws the page on under its floating address bar, past the window, so a scene sized to
     --screen left a band of page showing. Viewport units can't give it (100lvh measured just the
     window on an iPhone, and units that move would make the page jump), so it's the device's
     screen, the way up it's held (iOS reports it portrait-way round), measured with the rest.
     This runs while the page is still being parsed, before any of them is laid out. */
  if (touch) {
    let w = innerWidth, t = 0;
    const measure = () => {
      // the larger of the window and the layout viewport (which toolbars don't move), in case an
      // in-app browser reports a squashed window while the page is still loading
      const h = Math.max(innerHeight, root.clientHeight);
      const sides = [screen.width, screen.height], tall = innerWidth < innerHeight ? Math.max(...sides) : Math.min(...sides);
      root.style.setProperty('--screen', `${h}px`);
      root.style.setProperty('--screen-full', `${Math.max(h, tall)}px`);
      root.classList.toggle('is-short-screen', h < 700);
    };
    measure();
    // a turn shows up as a new width, either as a resize event or in the page's own box
    const turned = () => { if (innerWidth === w) return; w = innerWidth; clearTimeout(t); t = setTimeout(measure, 150); };
    addEventListener('resize', turned);
    new ResizeObserver(turned).observe(root);
  }

  /* ---------- helpers ---------- */
  const $ = (s, c = document) => c.querySelector(s);
  const $$ = (s, c = document) => [...c.querySelectorAll(s)];
  const esc = s => String(s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  // made once: toLocaleString builds a new formatter on every call, and every price on a page goes through here
  const euros = new Intl.NumberFormat('en-IE'), cents = new Intl.NumberFormat('en-IE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmt = n => '€' + (Number.isInteger(n) ? euros : cents).format(n);
  const icon = (n, cls = '') => `<svg class="ico ${cls}" viewBox="0 0 24 24" aria-hidden="true">${I[n] || ''}</svg>`;
  const stars = (n = 5) => `<span class="stars" aria-label="${n} out of 5 stars">${Array.from({ length: 5 }, (_, i) => `<svg class="ico ico--fill${i < Math.round(n) ? '' : ' ico--off'}" viewBox="0 0 24 24" aria-hidden="true">${I.star}</svg>`).join('')}</span>`;
  const qs = k => new URLSearchParams(location.search).get(k);
  // Keep rendered copy and search/social previews together.
  function seo({ title = document.title, description = $('meta[name="description"]')?.content || '', image, type, noindex } = {}) {
    const meta = (key, value, property = false) => {
      const attr = property ? 'property' : 'name';
      let el = $(`meta[${attr}="${key}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, key); document.head.appendChild(el); }
      el.content = value;
    };
    document.title = title;
    meta('description', description);
    meta('og:title', title, true);
    meta('og:description', description, true);
    meta('og:site_name', D.site.name, true);
    meta('og:locale', 'en_IE', true);
    if (type) meta('og:type', type, true);
    if (image) meta('og:image', new URL(image, location.href).href, true);
    if (noindex === true) meta('robots', 'noindex');
    else if (noindex === false) $('meta[name="robots"]')?.remove();
  }
  const page = window.AURA_PAGE || (location.pathname.split('/').pop() || 'index.html').replace(/\.html$/, '') || 'index';
  if (!D.shown(page)) location.replace(route('index.html'));   // a page that's switched off (data.js features)
  const avColors = ['#2588C9', '#0D1116', '#5A7D9A', '#17699F', '#3B4A5A', '#6E8CA6'];
  const initials = n => n.split(/\s+/).map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const avColor = n => avColors[[...n].reduce((a, c) => a + c.charCodeAt(0), 0) % avColors.length];

  // Paths from the brand SVG (assets/img/logo.svg)
  const LOGO_PATHS = {
    u1: 'M201.92,116v17.69c-13.54-1.63-25.4-8.15-35.36-19.4-11.49-12.94-17.42-28.78-17.6-47.09V2.06H165.4V67.12c.17,13.66,4.56,25.5,13.08,35.12,6.81,7.69,14.52,12.22,23.44,13.72',
    u2: 'M268.81,2.06V67.29c-.24,18.29-6.19,34.11-17.69,47-9.95,11.2-21.78,17.68-35.26,19.31V115.94c8.87-1.52,16.56-6,23.37-13.63,8.51-9.56,12.93-21.33,13.15-35V2.06Z',
    r: 'M283.73,132.87V63.95a61.87,61.87,0,0,1,18.61-45.32A62,62,0,0,1,347.5,0c2,0,4,.09,5.93.27L357,.6V17.86l-4.26-.41c-1.64-.16-3.36-.24-5.11-.24a44.84,44.84,0,0,0-33.07,13.69c-9.14,9.14-13.59,20-13.59,33.05v68.92Z',
    a2: 'M424.11,134.41a64.85,64.85,0,0,1-47.39-19.65,64.85,64.85,0,0,1-19.65-47.39A65.16,65.16,0,0,1,376.81,20a67,67,0,0,1,94.77,0,64.56,64.56,0,0,1,19.56,47.4V133H474.46V111.63q-1.42,1.6-3,3.13a64.85,64.85,0,0,1-47.39,19.65m0-117.38a48.3,48.3,0,0,0-35.64,14.72,48.28,48.28,0,0,0-14.7,35.62A48.29,48.29,0,0,0,388.49,103a50.51,50.51,0,0,0,71.27,0,48.29,48.29,0,0,0,14.7-35.63,48.3,48.3,0,0,0-14.72-35.64A48.29,48.29,0,0,0,424.11,17',
    a1: 'M67,134.41a64.85,64.85,0,0,1-47.39-19.65A64.85,64.85,0,0,1,0,67.38,65.16,65.16,0,0,1,19.73,20,64.85,64.85,0,0,1,67.12.34,64.86,64.86,0,0,1,114.51,20a64.56,64.56,0,0,1,19.56,47.4V133H117.38V111.63q-1.42,1.6-3,3.13A64.85,64.85,0,0,1,67,134.41M67,17A48.3,48.3,0,0,0,31.39,31.75a48.28,48.28,0,0,0-14.7,35.62A48.29,48.29,0,0,0,31.41,103,48.28,48.28,0,0,0,67,117.72,48.3,48.3,0,0,0,102.67,103a48.29,48.29,0,0,0,14.7-35.63,48.3,48.3,0,0,0-14.72-35.64A48.29,48.29,0,0,0,67,17',
  };
  const logoShapes = (attrs = {}) => ['u1', 'u2', 'r', 'a2', 'a1'].map(k => `<path ${attrs[k[0] === 'u' ? 'u' : 'ink'] || ''} d="${LOGO_PATHS[k]}"/>`).join('');
  const logo = (title = 'Aura Smart') => `<svg viewBox="0 0 491.15 134.41" role="img" aria-label="${title}">${logoShapes({ u: 'class="logo-blue"', ink: 'class="logo-ink"' })}</svg>`;
  // Header / mobile-menu logo: the "u" is top-lit with a soft gradient, and a light
  // sheen (masked to the letterforms) can sweep across it. Aura-header.js plays the sheen.
  let logoN = 0;
  const logoFx = (title = 'Aura Smart') => {
    const id = `lg${++logoN}`;
    return `<svg class="logo-fx" viewBox="0 0 491.15 134.41" role="img" aria-label="${title}">
      <defs>
        <linearGradient id="${id}u" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#5DB2E8"/><stop offset="1" stop-color="#1F7BBF"/></linearGradient>
        <linearGradient id="${id}s" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stop-color="#fff" stop-opacity="0"/><stop offset=".5" stop-color="#fff" stop-opacity=".7"/><stop offset="1" stop-color="#fff" stop-opacity="0"/></linearGradient>
        <mask id="${id}m" maskUnits="userSpaceOnUse" x="0" y="0" width="491.15" height="134.41">${logoShapes({ u: 'fill="#fff"', ink: 'fill="#fff"' })}</mask>
      </defs>
      ${logoShapes({ u: `class="logo-blue" style="fill:url(#${id}u)"`, ink: 'class="logo-ink"' })}
      <g mask="url(#${id}m)"><g class="logo-sheen"><rect x="-110" y="-20" width="90" height="175" fill="url(#${id}s)" transform="skewX(-18)"/></g></g>
    </svg>`;
  };
  // Footer wordmark, keeping watch at night: the bowls of the two a's are lenses
  // with a glint on their inner rim (aura-watch.js turns them), and a soft glow
  // behind the u breathes like a standby light. The u is top-lit like the header logo's.
  const watchMark = () => {
    // a 70° arc just inside each bowl, pointing right until it's turned
    const arc = 'M36.86,-25.81A45,45 0 0 1 36.86,25.81';
    const lens = ([x, y]) => `<g class="watch-lens" transform="translate(${x} ${y}) rotate(-90)"><circle r="50" fill="url(#lensSheen)"/><path class="glint-soft" d="${arc}" filter="url(#glintBlur)"/><path class="glint" d="${arc}"/></g>`;
    return `<svg viewBox="0 0 491.15 134.41" preserveAspectRatio="xMidYMax meet">
      <defs>
        <linearGradient id="watchU" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#5DB2E8"/><stop offset="1" stop-color="#1F7BBF"/></linearGradient>
        <linearGradient id="glintFade" gradientUnits="userSpaceOnUse" x1="0" y1="-26" x2="0" y2="26"><stop offset="0" stop-color="#A8D4F2" stop-opacity="0"/><stop offset=".5" stop-color="#E6F4FF"/><stop offset="1" stop-color="#A8D4F2" stop-opacity="0"/></linearGradient>
        <radialGradient id="lensSheen" gradientUnits="userSpaceOnUse" cx="24" cy="0" r="46"><stop offset="0" stop-color="#79BDEB" stop-opacity=".1"/><stop offset="1" stop-color="#79BDEB" stop-opacity="0"/></radialGradient>
        <filter id="glintBlur" x="-50%" y="-50%" width="200%" height="200%"><feGaussianBlur stdDeviation="2.4"/></filter>
        <filter id="breathBlur" x="-60%" y="-40%" width="220%" height="180%"><feGaussianBlur stdDeviation="8"/></filter>
      </defs>
      <g class="watch-breath" filter="url(#breathBlur)"><path d="${LOGO_PATHS.u1}"/><path d="${LOGO_PATHS.u2}"/></g>
      ${logoShapes({ u: 'class="logo-blue"', ink: 'class="logo-ink"' })}
      ${lens([67, 67.37])}${lens([424.11, 67.37])}
    </svg>`;
  };

  /* ---------- payment marks ----------
     The card-shaped acceptance marks Shopify's own themes use (activemerchant/payment_icons,
     MIT), drawn inline at 38 × 24. On Shopify the footer row becomes
     {%- for type in shop.enabled_payment_types -%}{{ type | payment_type_svg_tag: class: 'pay-icon' }}{%- endfor -%}
     so these show whatever payment methods the store has switched on. */
  const PAY = {
    visa: ["Visa", "0 0 38 24", '<rect x=".5" y=".5" width="37" height="23" rx="2.5" stroke="#000" stroke-opacity=".07" fill="none"/><path d="M35 0H3C1.3 0 0 1.3 0 3V21C0 22.7 1.4 24 3 24H35C36.7 24 38 22.7 38 21V3C38 1.3 36.6 0 35 0Z" fill="#142FBD"/><path d="M35 1C36.1 1 37 1.9 37 3V21C37 22.1 36.1 23 35 23H3C1.9 23 1 22.1 1 21V3C1 1.9 1.9 1 3 1H35Z" fill="#1532CB"/><path d="M29.5944 10.2167H29.2778C28.8556 11.2722 28.5389 11.8 28.2222 13.3833H30.2278C29.9111 11.8 29.9111 11.0611 29.5944 10.2167V10.2167ZM32.6556 16.4444H30.8611C30.7556 16.4444 30.7556 16.4444 30.65 16.3389L30.4389 15.3889L30.3333 15.1778H27.8C27.6944 15.1778 27.5889 15.1778 27.5889 15.3889L27.2722 16.3389C27.2722 16.4444 27.1667 16.4444 27.1667 16.4444H24.95L25.1611 15.9167L28.2222 8.73889C28.2222 8.21111 28.5389 8 29.0667 8H30.65C30.7556 8 30.8611 8 30.8611 8.21111L32.3389 15.0722C32.4444 15.4944 32.55 15.8111 32.55 16.2333C32.6556 16.3389 32.6556 16.3389 32.6556 16.4444V16.4444ZM18.5111 16.1278L18.9333 14.2278C19.0389 14.2278 19.1444 14.3333 19.1444 14.3333C19.8833 14.65 20.6222 14.8611 21.3611 14.7556C21.5722 14.7556 21.8889 14.65 22.1 14.5444C22.6278 14.3333 22.6278 13.8056 22.2056 13.3833C21.9944 13.1722 21.6778 13.0667 21.3611 12.8556C20.9389 12.6444 20.5167 12.4333 20.2 12.1167C18.9333 11.0611 19.3556 9.58333 20.0944 8.84444C20.7278 8.42222 21.0444 8 21.8889 8C23.1556 8 24.5278 8 25.1611 8.21111H25.2667C25.1611 8.84444 25.0556 9.37222 24.8444 10.0056C24.3167 9.79444 23.7889 9.58333 23.2611 9.58333C22.9444 9.58333 22.6278 9.58333 22.3111 9.68889C22.1 9.68889 21.9944 9.79444 21.8889 9.9C21.6778 10.1111 21.6778 10.4278 21.8889 10.6389L22.4167 11.0611C22.8389 11.2722 23.2611 11.4833 23.5778 11.6944C24.1056 12.0111 24.6333 12.5389 24.7389 13.1722C24.95 14.1222 24.6333 14.9667 23.7889 15.6C23.2611 16.0222 23.05 16.2333 22.3111 16.2333C20.8333 16.2333 19.6722 16.3389 18.7222 16.0222C18.6167 16.2333 18.6167 16.2333 18.5111 16.1278V16.1278ZM14.8167 16.4444C14.9222 15.7056 14.9222 15.7056 15.0278 15.3889C15.5556 13.0667 16.0833 10.6389 16.5056 8.31667C16.6111 8.10556 16.6111 8 16.8222 8H18.7222C18.5111 9.26667 18.3 10.2167 17.9833 11.3778C17.6667 12.9611 17.35 14.5444 16.9278 16.1278C16.9278 16.3389 16.8222 16.3389 16.6111 16.3389L14.8167 16.4444ZM5 8.21111C5 8.10556 5.21111 8 5.31667 8H8.90556C9.43333 8 9.85556 8.31667 9.96111 8.84444L10.9111 13.4889C10.9111 13.5944 10.9111 13.5944 11.0167 13.7C11.0167 13.5944 11.1222 13.5944 11.1222 13.5944L13.3389 8.21111C13.2333 8.10556 13.3389 8 13.4444 8H15.6611C15.6611 8.10556 15.6611 8.10556 15.5556 8.21111L12.2833 15.9167C12.1778 16.1278 12.1778 16.2333 12.0722 16.3389C11.9667 16.4444 11.7556 16.3389 11.5444 16.3389H9.96111C9.85556 16.3389 9.75 16.3389 9.75 16.1278L8.06111 9.58333C7.85 9.37222 7.53333 9.05556 7.11111 8.95C6.47778 8.63333 5.31667 8.42222 5.10556 8.42222L5 8.21111Z" fill="white"/>'],
    master: ["Mastercard", "0 0 38 24", '<rect x=".5" y=".5" width="37" height="23" rx="2.5" stroke="#000" stroke-opacity=".07" fill="none"/><path d="M35 0H3C1.3 0 0 1.3 0 3V21C0 22.7 1.4 24 3 24H35C36.7 24 38 22.7 38 21V3C38 1.3 36.6 0 35 0Z" fill="#1C1C1C"/><path d="M35 1C36.1 1 37 1.9 37 3V21C37 22.1 36.1 23 35 23H3C1.9 23 1 22.1 1 21V3C1 1.9 1.9 1 3 1H35Z" fill="#232323"/><path d="M14.6364 19.2727C18.8538 19.2727 22.2727 15.8538 22.2727 11.6364C22.2727 7.41892 18.8538 4 14.6364 4C10.4189 4 7 7.41892 7 11.6364C7 15.8538 10.4189 19.2727 14.6364 19.2727Z" fill="#EB001B"/><path d="M23.3637 19.2727C27.5811 19.2727 31 15.8538 31 11.6364C31 7.41892 27.5811 4 23.3637 4C19.1462 4 15.7273 7.41892 15.7273 11.6364C15.7273 15.8538 19.1462 19.2727 23.3637 19.2727Z" fill="#F79E1B"/><path d="M22.2727 11.6362C22.2727 9.01797 20.9637 6.72706 19 5.41797C17.0364 6.83615 15.7273 9.12706 15.7273 11.6362C15.7273 14.1452 17.0364 16.5452 19 17.8543C20.9637 16.5452 22.2727 14.2543 22.2727 11.6362Z" fill="#FF5F00"/>'],
    apple_pay: ["Apple Pay", "0 0 165.521 105.965", '<path fill="#000" d="M150.698 0H14.823c-.566 0-1.133 0-1.698.003-.477.004-.953.009-1.43.022-1.039.028-2.087.09-3.113.274a10.51 10.51 0 0 0-2.958.975 9.932 9.932 0 0 0-4.35 4.35 10.463 10.463 0 0 0-.975 2.96C.113 9.611.052 10.658.024 11.696a70.22 70.22 0 0 0-.022 1.43C0 13.69 0 14.256 0 14.823v76.318c0 .567 0 1.132.002 1.699.003.476.009.953.022 1.43.028 1.036.09 2.084.275 3.11a10.46 10.46 0 0 0 .974 2.96 9.897 9.897 0 0 0 1.83 2.52 9.874 9.874 0 0 0 2.52 1.83c.947.483 1.917.79 2.96.977 1.025.183 2.073.245 3.112.273.477.011.953.017 1.43.02.565.004 1.132.004 1.698.004h135.875c.565 0 1.132 0 1.697-.004.476-.002.952-.009 1.431-.02 1.037-.028 2.085-.09 3.113-.273a10.478 10.478 0 0 0 2.958-.977 9.955 9.955 0 0 0 4.35-4.35c.483-.947.789-1.917.974-2.96.186-1.026.246-2.074.274-3.11.013-.477.02-.954.022-1.43.004-.567.004-1.132.004-1.699V14.824c0-.567 0-1.133-.004-1.699a63.067 63.067 0 0 0-.022-1.429c-.028-1.038-.088-2.085-.274-3.112a10.4 10.4 0 0 0-.974-2.96 9.94 9.94 0 0 0-4.35-4.35A10.52 10.52 0 0 0 156.939.3c-1.028-.185-2.076-.246-3.113-.274a71.417 71.417 0 0 0-1.431-.022C151.83 0 151.263 0 150.698 0z" /><path fill="#FFF" d="M150.698 3.532l1.672.003c.452.003.905.008 1.36.02.793.022 1.719.065 2.583.22.75.135 1.38.34 1.984.648a6.392 6.392 0 0 1 2.804 2.807c.306.6.51 1.226.645 1.983.154.854.197 1.783.218 2.58.013.45.019.9.02 1.36.005.557.005 1.113.005 1.671v76.318c0 .558 0 1.114-.004 1.682-.002.45-.008.9-.02 1.35-.022.796-.065 1.725-.221 2.589a6.855 6.855 0 0 1-.645 1.975 6.397 6.397 0 0 1-2.808 2.807c-.6.306-1.228.511-1.971.645-.881.157-1.847.2-2.574.22-.457.01-.912.017-1.379.019-.555.004-1.113.004-1.669.004H14.801c-.55 0-1.1 0-1.66-.004a74.993 74.993 0 0 1-1.35-.018c-.744-.02-1.71-.064-2.584-.22a6.938 6.938 0 0 1-1.986-.65 6.337 6.337 0 0 1-1.622-1.18 6.355 6.355 0 0 1-1.178-1.623 6.935 6.935 0 0 1-.646-1.985c-.156-.863-.2-1.788-.22-2.578a66.088 66.088 0 0 1-.02-1.355l-.003-1.327V14.474l.002-1.325a66.7 66.7 0 0 1 .02-1.357c.022-.792.065-1.717.222-2.587a6.924 6.924 0 0 1 .646-1.981c.304-.598.7-1.144 1.18-1.623a6.386 6.386 0 0 1 1.624-1.18 6.96 6.96 0 0 1 1.98-.646c.865-.155 1.792-.198 2.586-.22.452-.012.905-.017 1.354-.02l1.677-.003h135.875" /><g><g><path fill="#000" d="M43.508 35.77c1.404-1.755 2.356-4.112 2.105-6.52-2.054.102-4.56 1.355-6.012 3.112-1.303 1.504-2.456 3.959-2.156 6.266 2.306.2 4.61-1.152 6.063-2.858" /><path fill="#000" d="M45.587 39.079c-3.35-.2-6.196 1.9-7.795 1.9-1.6 0-4.049-1.8-6.698-1.751-3.447.05-6.645 2-8.395 5.1-3.598 6.2-.95 15.4 2.55 20.45 1.699 2.5 3.747 5.25 6.445 5.151 2.55-.1 3.549-1.65 6.647-1.65 3.097 0 3.997 1.65 6.696 1.6 2.798-.05 4.548-2.5 6.247-5 1.95-2.85 2.747-5.6 2.797-5.75-.05-.05-5.396-2.101-5.446-8.251-.05-5.15 4.198-7.6 4.398-7.751-2.399-3.548-6.147-3.948-7.447-4.048" /></g><g><path fill="#000" d="M78.973 32.11c7.278 0 12.347 5.017 12.347 12.321 0 7.33-5.173 12.373-12.529 12.373h-8.058V69.62h-5.822V32.11h14.062zm-8.24 19.807h6.68c5.07 0 7.954-2.729 7.954-7.46 0-4.73-2.885-7.434-7.928-7.434h-6.706v14.894z" /><path fill="#000" d="M92.764 61.847c0-4.809 3.665-7.564 10.423-7.98l7.252-.442v-2.08c0-3.04-2.001-4.704-5.562-4.704-2.938 0-5.07 1.507-5.51 3.82h-5.252c.157-4.86 4.731-8.395 10.918-8.395 6.654 0 10.995 3.483 10.995 8.89v18.663h-5.38v-4.497h-.13c-1.534 2.937-4.914 4.782-8.579 4.782-5.406 0-9.175-3.222-9.175-8.057zm17.675-2.417v-2.106l-6.472.416c-3.64.234-5.536 1.585-5.536 3.95 0 2.288 1.975 3.77 5.068 3.77 3.95 0 6.94-2.522 6.94-6.03z" /><path fill="#000" d="M120.975 79.652v-4.496c.364.051 1.247.103 1.715.103 2.573 0 4.029-1.09 4.913-3.899l.52-1.663-9.852-27.293h6.082l6.863 22.146h.13l6.862-22.146h5.927l-10.216 28.67c-2.34 6.577-5.017 8.735-10.683 8.735-.442 0-1.872-.052-2.261-.157z" /></g></g>'],
    google_pay: ["Google Pay", "0 0 38 24", '<path d="M35 0H3C1.3 0 0 1.3 0 3v18c0 1.7 1.4 3 3 3h32c1.7 0 3-1.3 3-3V3c0-1.7-1.4-3-3-3z" fill="#000" opacity=".07"/><path d="M35 1c1.1 0 2 .9 2 2v18c0 1.1-.9 2-2 2H3c-1.1 0-2-.9-2-2V3c0-1.1.9-2 2-2h32" fill="#FFF"/><path d="M18.093 11.976v3.2h-1.018v-7.9h2.691a2.447 2.447 0 0 1 1.747.692 2.28 2.28 0 0 1 .11 3.224l-.11.116c-.47.447-1.098.69-1.747.674l-1.673-.006zm0-3.732v2.788h1.698c.377.012.741-.135 1.005-.404a1.391 1.391 0 0 0-1.005-2.354l-1.698-.03zm6.484 1.348c.65-.03 1.286.188 1.778.613.445.43.682 1.03.65 1.649v3.334h-.969v-.766h-.049a1.93 1.93 0 0 1-1.673.931 2.17 2.17 0 0 1-1.496-.533 1.667 1.667 0 0 1-.613-1.324 1.606 1.606 0 0 1 .613-1.336 2.746 2.746 0 0 1 1.698-.515c.517-.02 1.03.093 1.49.331v-.208a1.134 1.134 0 0 0-.417-.901 1.416 1.416 0 0 0-.98-.368 1.545 1.545 0 0 0-1.319.717l-.895-.564a2.488 2.488 0 0 1 2.182-1.06zM23.29 13.52a.79.79 0 0 0 .337.662c.223.176.5.269.785.263.429-.001.84-.17 1.146-.472.305-.286.478-.685.478-1.103a2.047 2.047 0 0 0-1.324-.374 1.716 1.716 0 0 0-1.03.294.883.883 0 0 0-.392.73zm9.286-3.75l-3.39 7.79h-1.048l1.281-2.728-2.224-5.062h1.103l1.612 3.885 1.569-3.885h1.097z" fill="#5F6368"/><path d="M13.986 11.284c0-.308-.024-.616-.073-.92h-4.29v1.747h2.451a2.096 2.096 0 0 1-.9 1.373v1.134h1.464a4.433 4.433 0 0 0 1.348-3.334z" fill="#4285F4"/><path d="M9.629 15.721a4.352 4.352 0 0 0 3.01-1.097l-1.466-1.14a2.752 2.752 0 0 1-4.094-1.44H5.577v1.17a4.53 4.53 0 0 0 4.052 2.507z" fill="#34A853"/><path d="M7.079 12.05a2.709 2.709 0 0 1 0-1.735v-1.17H5.577a4.505 4.505 0 0 0 0 4.075l1.502-1.17z" fill="#FBBC04"/><path d="M9.629 8.44a2.452 2.452 0 0 1 1.74.68l1.3-1.293a4.37 4.37 0 0 0-3.065-1.183 4.53 4.53 0 0 0-4.027 2.5l1.502 1.171a2.715 2.715 0 0 1 2.55-1.875z" fill="#EA4335"/>'],
  };
  const payIcon = k => { const [name, vb, body] = PAY[k]; return `<svg class="pay-icon" viewBox="${vb}" role="img" aria-label="${name}">${body}</svg>`; };

  /* ---------- cart store ---------- */
  const KEY = 'aura-cart-v1';
  // a saved line for a product that has left the catalogue is dropped, not shown broken
  const load = () => { try { return (JSON.parse(localStorage.getItem(KEY)) || []).filter(i => D.byHandle(i?.handle)); } catch { return []; } };
  const save = items => { try { localStorage.setItem(KEY, JSON.stringify(items)); } catch {} };
  const Cart = window.createAuraShopifyCart ? window.createAuraShopifyCart(D) : {
    items: load(),
    key: (h, v, c) => [h, v, c || ''].join('|'),
    priceOf(line) { const p = D.byHandle(line.handle); return p ? p.price[line.variant === 'diy' ? 1 : 0] : 0; },
    add(handle, variant = 'pro', color = null, qty = 1) {
      const p = D.byHandle(handle); if (!p) return;
      if (p.colors && !color) color = p.colors[0].name;
      const k = this.key(handle, variant, color);
      const ex = this.items.find(i => i.key === k);
      if (ex) ex.qty += qty; else this.items.push({ key: k, handle, variant, color, qty });
      this.commit();
      bumpBadge();
    },
    set(key, qty) { const it = this.items.find(i => i.key === key); if (!it) return; it.qty = Math.max(0, Math.min(20, qty)); if (!it.qty) this.items = this.items.filter(i => i !== it); this.commit(); },
    remove(key) { this.items = this.items.filter(i => i.key !== key); this.commit(); },
    // switch a line between Pro and DIY where it stands (merging if that version is already in the cart)
    setVariant(key, variant) {
      const it = this.items.find(i => i.key === key); if (!it || it.variant === variant) return;
      const k = this.key(it.handle, variant, it.color), dup = this.items.find(i => i.key === k);
      if (dup) { dup.qty = Math.min(20, dup.qty + it.qty); this.items = this.items.filter(i => i !== it); }
      else { it.variant = variant; it.key = k; }
      this.commit();
    },
    clear() { this.items = []; this.commit(); },
    count() { return this.items.reduce((a, i) => a + i.qty, 0); },
    subtotal() { return this.items.reduce((a, i) => a + this.priceOf(i) * i.qty, 0); },
    compareTotal() { return this.items.reduce((a, i) => a + (D.byHandle(i.handle)?.compare || this.priceOf(i)) * i.qty, 0); },
    hasPro() { return this.items.some(i => i.variant === 'pro'); },
    commit() { save(this.items); document.dispatchEvent(new CustomEvent('cart:change')); },
  };
  window.addEventListener('storage', e => { if (!window.createAuraShopifyCart && e.key === KEY) { Cart.items = load(); document.dispatchEvent(new CustomEvent('cart:change')); } });

  /* ---------- recently viewed (product.html records each visit, newest first) ---------- */
  const RECENT = 'aura-recent';
  const recent = (exclude = []) => {
    let list = []; try { list = JSON.parse(localStorage.getItem(RECENT)) || []; } catch {}
    return list.filter(h => !exclude.includes(h)).map(D.byHandle).filter(Boolean);
  };
  const markViewed = handle => { try { const r = [handle, ...recent([handle]).map(p => p.handle)]; localStorage.setItem(RECENT, JSON.stringify(r.slice(0, 8))); } catch {} };

  /* ---------- product card ---------- */
  // First bubble: what kind of install the product needs, by handle. Only products whose
  // description states it (or the owner confirmed); anything unclear gets no feature bubble.
  const CARD_FEATURE = {
    'solar-camera-pro': 'Wire-Free',
    'solar-wall-light-camera': 'Wire-Free',
    'floodlight-camera': 'Wired',
    'wired-camera-pro': 'Wired',
    'video-door-phone': 'With Indoor Monitor'
  };
  // Second bubble: cameras and the video doorbell ship with an SD card (not locks or alarm)
  // Smart lock bubbles (shown alongside its 'New' badge)
  const CARD_LOCK = ['smart-lock-slim', 'smart-lock'];
  const CARD_SD = ['solar-camera-pro', 'solar-wall-light-camera', 'floodlight-camera', 'wired-camera-pro', 'indoorcam-p1', 'video-door-phone'];
  // Card price, as on the product page: installed (Pro) variant is the headline, the DIY variant
  // underneath, and a saving only where the installed variant's compare-at is above its price.
  function cardPrice(p) {
    const vs = p.variants || [];
    const pro = vs.find(v => v.mode === 'pro');
    const diy = vs.find(v => v.mode === 'diy');
    if (vs.length) {
      const main = pro || diy || vs[0];
      const compare = +main.compare || 0;
      const installed = +main.price || 0;
      return { installed, diy: pro && diy ? +diy.price : null, compare, save: compare > installed ? compare - installed : 0 };
    }
    const installed = +(p.price && p.price[0]) || 0;
    const d = +(p.price && p.price[1]) || 0;
    const compare = +p.compare || 0;
    return { installed, diy: d && d !== installed ? d : null, compare, save: compare > installed ? compare - installed : 0 };
  }

  function productCard(p, { idx = 0, alt = true, mode = 'from' } = {}) {
    // the second photo only shows on hover (main.css), so a touch screen, which can't hover, never loads it
    const altImg = alt && !touch && p.hover;
    const pr = cardPrice(p);
    const feature = CARD_FEATURE[p.handle];
    const chips = feature
      ? `<span class="badge">${esc(feature)}</span>`
      : p.badge && p.badge !== 'Wire-free' && p.badge !== 'Wired' ? `<span class="badge ${p.badge === 'New' ? 'badge--blue' : p.badge === 'Bestseller' ? 'badge--ink' : ''}">${esc(p.badge)}</span>` : '';
    const sd = CARD_SD.includes(p.handle) ? '<span class="badge">SD Card Included</span>' : '';
    const lockChips = CARD_LOCK.includes(p.handle) ? '<span class="badge">Fits Most Irish Doors</span><span class="badge">Rustproof</span>' : '';
    return `
    <article class="pcard ${altImg ? 'has-alt' : ''}" data-reveal style="--d:${(idx % 4) * 80}">
      <div class="pcard__media" style="--tint:${p.tint}">
        <div class="pcard__badges"><span class="pcard__chips">${chips}${sd}${lockChips}</span>${pr.save > 0 ? `<span class="badge badge--save">Save ${fmt(pr.save)}</span>` : ''}</div>
        <div class="pcard__img pcard__img--main"><img src="${p.images[0]}" alt="${esc(p.name)}" loading="lazy" width="600" height="600"></div>
        ${altImg ? `<div class="pcard__img pcard__img--alt"><img src="${altImg}" alt="" loading="lazy" style="object-position:${p.hoverAt}"></div>` : ''}
        <div class="pcard__quick"><button class="btn btn--light btn--noarrow" type="button" data-quick="${p.handle}">${icon('plus')} Quick add</button></div>
        <button class="pcard__add" type="button" data-quick="${p.handle}" aria-label="Quick add ${esc(p.name)}">${icon('plus')}</button>
      </div>
      <div class="pcard__body">
        <div class="pcard__row">
          <h3 class="pcard__name"><a href="${route(`product.html?p=${p.handle}`)}">${esc(p.name)}</a></h3>
          ${p.colors ? `<div class="pcard__swatches" aria-label="${p.colors.length} finishes" data-handle="${p.handle}">${A.cardColors(p.colors).map(c => `<span class="swatch-dot" style="--c:${c.hex}" role="img" aria-label="${esc(c.name)}" data-color="${esc(c.name)}"></span>`).join('')}</div>` : ''}
        </div>
        <div class="pcard__type">${esc(p.tagline)}</div>
        <div class="pcard__row pcard__foot">
          <span class="price pcard__price"><span class="pcard__now">${pr.diy != null ? '<span class="sr-only">Installed price </span>' : ''}${fmt(pr.installed)} ${pr.save > 0 ? `<s><span class="sr-only">was </span>${fmt(pr.compare)}</s>` : ''}</span>${pr.diy != null ? `<small class="pcard__diy">DIY ${fmt(pr.diy)}</small>` : ''}</span>
          ${p.rating ? `<span class="pcard__meta" aria-label="${p.rating.value} out of 5 from ${p.rating.count} product reviews">${icon('star', 'ico--fill')}${p.rating.value.toFixed(1)}</span>` : ''}
        </div>
      </div>
    </article>`;
  }


  /* ---------- shared renderers ---------- */
  // A review from Google or Facebook (Shopify's `reviews-data` snippet, window.AURA_REVIEWS) says
  // where it was written; a Facebook recommendation has no stars. Long ones are cut to the length of the
  // longest testimonial the rows were designed with, so one review doesn't make its row tall.
  const REVIEW_SITES = { google: 'Google review', facebook: 'Facebook review' };
  const clip = (t, n = 220) => t.length > n ? t.slice(0, t.lastIndexOf(' ', n)).replace(/[\s,.;:!?–—-]+$/, '') + '…' : t;
  const reviewCard = r => {
    const show = window.AURA_REVIEWS?.show || {};
    const site = REVIEW_SITES[r.source], link = site && window.AURA_REVIEWS?.links?.[r.source];
    const mark = site && show.source !== false ? (link ? `<a class="rcard__src" href="${esc(link)}" target="_blank" rel="noopener">${icon(r.source)} ${site}</a>` : `<span class="rcard__src">${icon(r.source)} ${site}</span>`) : `<span>${icon('check-circle')} Aura Smart customer</span>`;
    const score = site && r.rating == null ? `<span class="rcard__rec">${icon('check-circle')} Recommends</span>` : stars(r.rating ?? 5);
    const tag = r.tag && show.tags !== false ? `<span class="pill pill--blue rcard__tag">${esc(r.tag)}</span>` : '';
    return `<figure class="rcard"><div class="rcard__top">${score}${tag}</div><blockquote class="rcard__text">“${esc(clip(r.text))}”</blockquote><figcaption class="rcard__who"><span class="avatar" style="--av:${avColor(r.name)}">${initials(r.name)}</span><div><b>${esc(r.name)}</b>${mark}</div></figcaption></figure>`;
  };
  // Rows gliding opposite ways, laid out as theme settings → Reviews says (window.AURA_REVIEWS.show):
  // "split", Google on top and Facebook below ("split_reverse" the other way round); "mixed", both
  // sites newest first, shared between the rows; "google" or "facebook", that site only. One row holds
  // them all in the same order, as does one site with under 6 reviews. Without live reviews, the given
  // reviews. A short row repeats its cards so it still fills a wide screen.
  const reviewRows = (list = D.reviews) => {
    const live = window.AURA_REVIEWS || {}, show = live.show || {};
    const per = show.perRow || 12, two = (show.rows || 2) > 1;
    const newest = l => [...(l || [])].sort((a, b) => (b.date || '').localeCompare(a.date || ''));
    const g = newest(live.google).slice(0, per), f = newest(live.facebook).slice(0, per);
    const halves = l => { const half = Math.ceil(l.length / 2); return [l.slice(0, half), l.slice(half)]; };
    const alternate = l => [l.filter((_, i) => i % 2 === 0), l.filter((_, i) => i % 2)];
    let rows;
    if (!g.length && !f.length) rows = two ? halves(list) : [list];
    else if (show.layout === 'mixed') { const all = newest([...g, ...f]).slice(0, two ? per * 2 : per); rows = two ? alternate(all) : [all]; }
    else {
      const [a, b] = show.layout === 'split_reverse' ? [f, g] : [g, f];
      const one = a.length ? a : b;
      rows = a.length && b.length ? (two ? [a, b] : [[...a, ...b].slice(0, per)]) : (two && one.length >= 6 ? halves(one) : [one]);
    }
    const fill = row => { let out = row; while (row.length && out.length < 4) out = out.concat(row); return out; };
    return rows.filter(row => row.length).map((row, i) => `<div class="marquee ${i ? 'marquee--rev' : ''}"><div class="marquee__track">${fill(row).map(reviewCard).join('')}</div></div>`).join('');
  };
  const faqHTML = (items, openFirst = true) => items.map(([q, a], i) => `<details class="acc__item" ${openFirst && i === 0 ? 'open' : ''}><summary>${esc(q)}<span class="acc__icon">${icon('plus')}</span></summary><div class="acc__content"><div>${esc(a)}</div></div></details>`).join('');
  const jcard = (a, i = 0) => `<article class="jcard" data-reveal style="--d:${(i % 3) * 100}"><div class="jcard__media"><img src="${a.image}" alt="" loading="lazy"></div><div class="jcard__meta"><span class="pill">${esc(a.tag)}</span><span>${a.minutes} min read</span></div><h3><a href="${route(`article.html?a=${a.slug}`)}">${esc(a.title)}</a></h3></article>`;

  /* ---------- chrome markup ---------- */
  // Announcement bar messages (on Shopify: blocks in the announcement section).
  // `short` is the phone version, so nothing gets cut off with an ellipsis.
  const announceMsgs = [
    { icon: 'sparkle', text: 'Planning several devices? Ask for a bundle quote', short: 'Ask for a bundle quote', cta: 'Get a quote', href: 'contact.html#quote' },
    { icon: 'euro', text: 'Use local recordings without a required subscription', short: 'No required subscription', cta: 'Storage details', href: 'faq.html?q=monthly%20fees' },
    { icon: 'tools', text: 'Pro installation across Dublin, Cork &amp; Galway', short: 'Pro install: Dublin, Cork &amp; Galway', cta: 'See fitted prices', href: 'pro-installation.html' },
    { icon: 'shield-plain', text: '18-month warranty on every device', short: '18-month warranty on everything', cta: 'Warranty terms', href: 'faq.html?q=warranty' },
  ];

  // Markup only (the future sections/header.liquid). Behaviour lives in
  // src/header/aura-header.js, which upgrades <aura-header> when it loads.
  function headerHTML() {
    const cols = D.collections.filter(c => c.handle !== 'all');
    const featured = ['floodlight-camera', 'smart-lock', 'solar-camera-pro', 'indoorcam-p1'].map(D.byHandle);
    const cap = s => s.replace(/^./, m => m.toUpperCase());
    const onShop = page === 'shop' || page === 'product';
    const curCat = page === 'shop' ? qs('c') || 'all' : null;
    const prod = (p, pair) => `<a class="mega__prod" href="${route(`product.html?p=${p.handle}`)}"><span class="media" style="--tint:${p.tint}"><img src="${p.images[0]}" alt="" loading="lazy">${pair ? '<span class="mega__tag">Pairs well</span>' : ''}</span><b>${p.name}</b><span>${fmt(p.from)}</span></a>`;
    // One panel per category. Small categories are topped up with bestsellers that pair well with them.
    const panel = (handle, title, own, total) => {
      const fill = featured.filter(p => !own.includes(p)).slice(0, Math.max(0, 4 - own.length));
      return `<div class="mega__panel" data-mega-panel="${handle}" ${handle === 'all' ? '' : 'hidden'}>
        <div class="mega__head"><b>${title}</b><a class="link small" href="${route(`shop.html${handle === 'all' ? '' : '?c=' + handle}`)}">View all ${total} ${icon('arrow')}</a></div>
        <div class="mega__products">${own.slice(0, 4).map(p => prod(p)).join('')}${fill.map(p => prod(p, true)).join('')}</div>
      </div>`;
    };
    return `
    <a class="skip-link" href="#main">Skip to content</a>
    <aura-announce class="announce" role="region" aria-label="Announcements">
      <div class="container announce__inner">
        <div class="announce__side"><a href="${D.site.phoneHref}">${icon('phone')} ${D.site.phone}</a><a href="${D.site.whatsappHref}" target="_blank" rel="noopener">${icon('whatsapp')} WhatsApp us</a></div>
        <div class="announce__center">
          <button class="announce__nav" type="button" data-announce-step="-1" aria-label="Previous announcement">${icon('chevron-left')}</button>
          <div class="announce__track" aria-live="off">${announceMsgs.map((m, i) => `<a class="announce__msg ${i === 0 ? 'is-active' : ''}" href="${route(m.href)}" ${i === 0 ? '' : 'inert'}>${icon(m.icon)}<span class="announce__text"><span class="announce__long">${m.text}</span><span class="announce__short">${m.short}</span></span><span class="announce__cta">${m.cta}${icon('arrow')}</span></a>`).join('')}</div>
          <button class="announce__nav" type="button" data-announce-step="1" aria-label="Next announcement">${icon('chevron-right')}</button>
        </div>
        <div class="announce__side"><a class="announce__rating" href="${route(`index.html#reviews`)}">${stars()}<span><b>${D.site.rating}</b> from ${D.site.reviewCount} reviews</span>${icon('arrow')}</a></div>
      </div>
    </aura-announce>
    <header class="site-header" id="siteHeader">
      <aura-header class="container">
        <nav class="nav-bar" aria-label="Main">
          <span class="nav-pill" aria-hidden="true"></span>
          <a class="brand" href="${route(`index.html`)}" aria-label="Aura Smart home">${logoFx()}</a>
          <ul class="nav-links" role="list">
            ${D.nav.map(n => n.mega
              ? `<li class="nav-mega"><button type="button" aria-expanded="false" aria-controls="mega" data-mega-toggle ${onShop ? 'data-current' : ''}>${n.label} ${icon('chevron')}</button>${megaHTML()}</li>`
              : `<li><a href="${route(n.href)}" ${page === n.href.replace('.html', '') ? 'aria-current="page"' : ''}>${n.label}</a></li>`).join('')}
          </ul>
          <div class="nav-actions">
            <button class="icon-btn" type="button" data-search-open aria-label="Search">${icon('search')}</button>
            <a class="icon-btn hide-sm" href="${D.site.whatsappHref}" target="_blank" rel="noopener" aria-label="WhatsApp us">${icon('whatsapp')}</a>
            <button class="icon-btn" type="button" data-cart-open aria-label="Open cart">${icon('bag')}<span class="count-badge" data-cart-count>0</span></button>
            <a class="btn btn--sm btn--blue nav-cta" href="${route(`contact.html#quote`)}">Free quote</a>
            <button class="icon-btn nav-toggle" type="button" data-mnav-open aria-label="Open menu" aria-expanded="false">${icon('menu')}</button>
          </div>
        </nav>
      </aura-header>
    </header>
    <div class="mega-scrim" data-mega-scrim></div>`;

    // Lives inside the Shop <li> so Tab moves from the Shop button straight into the menu
    function megaHTML() {
      const catLink = (handle, title, n) => `<a class="mega__col-link ${handle === 'all' ? 'is-active' : ''}" href="${route(`shop.html${handle === 'all' ? '' : '?c=' + handle}`)}" data-mega-cat="${handle}" ${curCat === handle ? 'aria-current="page"' : ''}>${title} <small>${n}</small></a>`;
      return `
          <div class="mega" id="mega" role="region" aria-label="Shop menu">
            <div class="mega__cols">
              <span class="eyebrow">Shop by category</span>
              ${catLink('all', 'Shop all', D.products.length)}
              ${cols.map(c => catLink(c.handle, cap(c.title), D.inCollection(c.handle).length)).join('')}
            </div>
            <div class="mega__panels">
              ${panel('all', 'Bestsellers', featured, D.products.length)}
              ${cols.map(c => panel(c.handle, cap(c.title), D.inCollection(c.handle), D.inCollection(c.handle).length)).join('')}
            </div>
            <a class="mega__promo" href="${route(`pro-installation.html`)}">
              <img src="${D.asset('assets/img/card-pro-install.webp')}" alt="" loading="lazy">
              <span class="pill pill--glass">${icon('tools')} Pro installation</span>
              <h4>Fitted by licensed installers</h4>
              <p>Dublin, Cork, Galway and surrounding areas. Typically 40 minutes to 2 hours.</p>
            </a>
          </div>`;
    }
  }

  // Mobile menu: search, then shopping (swipeable categories), then the rest.
  // The quote button sits in a footer outside the scroll area so it's always in reach.
  function mnavHTML() {
    const links = [['Pro installation', 'pro-installation.html', 'Book'], ['App', 'index.html#app', 'iPhone & Android'], ['Smart blinds', 'smart-blinds.html', 'New'], ['Blackout blinds', 'blackout-blinds.html', 'Guard'], ['Showroom', 'showroom.html', 'Bray'], ['About', 'about.html', ''], ['Journal', 'journal.html', ''], ['Support & FAQ', 'faq.html', ''], ['Contact', 'contact.html', '']].filter(l => D.shown(l[1]));
    const cats = D.collections.filter(c => c.handle !== 'all');
    const curCat = page === 'shop' ? qs('c') : null;
    const here = href => page === href.replace('.html', '');
    return `
    <div class="mnav" id="mnav" role="dialog" aria-modal="true" aria-label="Menu">
      <div class="mnav__top"><a class="brand" href="${route(`index.html`)}" aria-label="Aura Smart home">${logoFx()}</a><button class="icon-btn icon-btn--solid" type="button" data-mnav-close aria-label="Close menu">${icon('close')}</button></div>
      <div class="mnav__body">
        <button class="mnav__search mnav__in" type="button" data-search-open style="--i:0">${icon('search')}<span>Search products, answers…</span></button>
        <section class="mnav__shop mnav__in" aria-labelledby="mnavShop" style="--i:1">
          <div class="mnav__head"><h2 class="eyebrow" id="mnavShop">Shop</h2><a class="link small" href="${route(`shop.html`)}" ${page === 'shop' && !curCat ? 'aria-current="page"' : ''}>Shop all ${D.products.length} ${icon('arrow')}</a></div>
          <div class="mnav__cats">${cats.map(c => `<a class="mnav__cat" href="${route(`shop.html?c=${c.handle}`)}" ${curCat === c.handle ? 'aria-current="page"' : ''}><span class="media"><img src="${c.image}" alt="" loading="lazy"></span><span class="mnav__cat-name">${c.title.replace(/^./, m => m.toUpperCase())} <small>${D.inCollection(c.handle).length}</small></span></a>`).join('')}</div>
        </section>
        <nav class="mnav__links" aria-label="Menu">${links.map((l, i) => `<a class="mnav__in" href="${route(l[1])}" style="--i:${i + 2}" ${here(l[1]) ? 'aria-current="page"' : ''}>${l[0]} <small>${here(l[1]) ? 'You’re here' : l[2]}</small></a>`).join('')}</nav>
        <div class="mnav__contact mnav__in" style="--i:${links.length + 2}">
          <a href="${D.site.whatsappHref}" target="_blank" rel="noopener">${icon('whatsapp')}<b>WhatsApp</b>${D.site.whatsapp}</a>
          <a href="${D.site.phoneHref}">${icon('phone')}<b>Call us</b>${D.site.phone}</a>
        </div>
      </div>
      <div class="mnav__foot"><a class="btn btn--blue btn--block" href="${route(`contact.html#quote`)}">Get a free quote</a></div>
    </div>`;
  }

  function overlaysHTML() {
    return `
    <div class="scrim" data-scrim></div>
    <aside class="drawer" id="cartDrawer" role="dialog" aria-modal="true" aria-labelledby="cartTitle" aria-hidden="true">
      <div class="drawer__head"><h2 id="cartTitle">Your cart <small data-cart-count-text></small></h2><button class="icon-btn" type="button" data-cart-close aria-label="Close cart">${icon('close')}</button></div>
      <div class="drawer__body" data-cart-body></div>
      <div class="drawer__foot" data-cart-foot></div>
    </aside>
    <div class="modal" id="quickModal" role="dialog" aria-modal="true" aria-label="Quick add" aria-hidden="true"><div class="modal__panel"><button class="icon-btn icon-btn--solid modal__close" type="button" data-modal-close aria-label="Close">${icon('close')}</button><div data-modal-body></div></div></div>
    <aura-search class="search" id="search" role="dialog" aria-modal="true" aria-label="Search" aria-hidden="true">
      <div class="search__scrim" data-search-close></div>
      <div class="search__panel">
        <div class="search__field">${icon('search')}<input type="search" placeholder="Search products, answers, guides… try “camera under 150”" aria-label="Search the store" role="combobox" aria-expanded="true" aria-controls="searchResults" aria-autocomplete="list" data-search-input autocomplete="off" spellcheck="false" enterkeyhint="search"><kbd>Esc</kbd><button class="icon-btn" type="button" data-search-close aria-label="Close search">${icon('close')}</button></div>
        <div class="search__results" id="searchResults" role="listbox" aria-label="Search results" data-search-results></div>
        <p class="sr-only" aria-live="polite" data-search-status></p>
      </div>
    </aura-search>
    <div class="toast-wrap" aria-live="polite" data-toasts></div>
    <a class="fab" href="${D.site.whatsappHref}" target="_blank" rel="noopener" aria-label="Chat with us on WhatsApp"><span class="wa">${icon('whatsapp')}</span><span class="fab__label"><small>Need help?</small><b>Chat on WhatsApp</b></span></a>`;
  }

  const FOOT_PHONE = '(max-width: 699px)';   // footer link groups collapse below this (main.css matches)
  function footerHTML() {
    // each link group is a disclosure: tap-to-open on phones, always open from 700px (initFootCols)
    const open = !matchMedia(FOOT_PHONE).matches;
    // the page you're on (a link with a #fragment is a place on a page, never the page itself)
    const here = href => { if (/^[a-z]+:|#/.test(href)) return false; const u = new URL(href, location.href); return u.pathname.split('/').pop().replace(/\.html$/, '') === page && u.search === location.search; };
    const link = ([label, href, cls]) => `<li><a ${cls ? `class="${cls}" ` : ''}href="${route(href)}"${here(href) ? ' aria-current="page"' : ''}><span>${label}</span></a></li>`;
    const col = (title, links, more = '', cls = '') => `<details class="foot-col${cls}${open ? ' is-open' : ''}"${open ? ' open' : ''}><summary><h3>${title}</h3></summary><div class="foot-col__body"><ul>${links.filter(l => D.shown(l[1])).map(link).join('')}</ul>${more}</div></details>`;
    const social = (label, href, ico) => `<a href="${route(href)}" target="_blank" rel="noopener" aria-label="${label}">${icon(ico)}</a>`;
    const talk = col('Talk to us', [[D.site.phone, D.site.phoneHref, 'foot-tel'], [D.site.email, 'mailto:' + D.site.email], ['Showroom, ' + D.site.showroom.town, 'showroom.html']],
      `<div class="foot-social">${social('Aura Smart on Facebook', D.site.facebook, 'facebook')}${social('Aura Smart on Instagram', D.site.instagram, 'instagram')}</div>`, ' foot-col--talk')
      // the email can be copied as well as opened (a mouse only: on a phone, press and hold the address)
      .replace(`<li><a href="mailto:${D.site.email}"`, `<li class="foot-has-copy"><a href="mailto:${D.site.email}"`)
      .replace(`<span>${D.site.email}</span></a>`, `<span>${D.site.email}</span></a><button class="foot-copy" type="button" data-copy="${D.site.email}" aria-label="Copy the email address">${icon('copy')}${icon('check')}</button>`);
    return `
    <footer class="site-footer on-dark">
      <div class="container">
        <div class="foot-top">
          <div class="foot-news">
            <h2>Get Aura Smart <span class="accent">updates</span></h2>
            <p id="newsHint">Enter your email below for product news, security tips and offers. Unsubscribe at any time.</p>
            <form class="news-form" data-news-form novalidate><label class="sr-only" for="newsEmail">Email address</label><input id="newsEmail" type="text" inputmode="email" autocomplete="email" autocapitalize="off" spellcheck="false" enterkeyhint="send" placeholder="Your email address" aria-describedby="newsHint newsNote" aria-required="true"><button class="btn btn--light btn--sm" type="submit">Try signup form</button></form>
            <p class="news-form__err" id="newsErr" role="alert" hidden></p>
            <p class="news-note" id="newsNote">By signing up, you agree to our <a href="${route(`policy.html?p=privacy`)}">privacy policy</a>.</p>
          </div>
          <div class="foot-cols">
            ${col('Shop', [['All products', 'shop.html'], ['Security cameras', 'shop.html?c=cameras'], ['Floodlight cameras', 'shop.html?c=floodlights'], ['Smart locks', 'shop.html?c=locks'], ['House alarms', 'shop.html?c=alarms'], ['Smart control panels', 'shop.html?c=panels'], ['Smart sockets & switches', 'shop.html?c=power']])}
            ${col('Services', [['Pro installation', 'pro-installation.html'], ['Smart blinds', 'smart-blinds.html'], ['Blackout blinds', 'blackout-blinds.html'], ['Upgrade programme', 'upgrade.html'], ['Free quote', 'contact.html#quote']])}
            ${col('Company', [['About us', 'about.html'], ['Journal', 'journal.html'], ['FAQ & support', 'faq.html'], ['Contact', 'contact.html']])}
            ${talk}
          </div>
        </div>
      </div>
      <div class="foot-sky">
        <aura-sky aria-hidden="true"><canvas></canvas></aura-sky>
        <div class="container">
        <aura-watch class="foot-mark" aria-hidden="true">${watchMark()}</aura-watch>
        <div class="foot-bottom">
          <p class="foot-copy">© ${new Date().getFullYear()} Aura Smart. All rights reserved. <span>Formerly ${D.site.formerly}.</span></p>
          <nav class="foot-legal" aria-label="Legal"><a href="${route(`policy.html?p=refund`)}">Refund policy</a><a href="${route(`policy.html?p=privacy`)}">Privacy policy</a><a href="${route(`policy.html?p=terms`)}">Terms of service</a></nav>
          <ul class="pay-icons" role="list" aria-label="Payment methods">${['visa', 'master', 'apple_pay', 'google_pay'].map(k => `<li>${payIcon(k)}</li>`).join('')}<li class="pay-cash">Cash on install</li></ul>
        </div>
        </div>
      </div>
    </footer>`;
  }

  /* ---------- mount chrome ---------- */
  // Header mounts immediately (app.js is loaded right after #chrome-top) so it is
  // in place before first paint; the footer + overlays mount on DOMContentLoaded.
  function mountTop() { const top = document.getElementById('chrome-top'); if (top) top.outerHTML = headerHTML() + mnavHTML(); }
  function mountFoot() {
    if (window.AURA_SHOPIFY) return; // Liquid section groups already own the chrome and overlays.
    const foot = document.getElementById('chrome-footer');
    const minimal = document.body.dataset.chrome === 'minimal';
    const html = (minimal ? '' : footerHTML()) + overlaysHTML();
    if (foot) foot.outerHTML = html; else document.body.insertAdjacentHTML('beforeend', html);
  }
  mountTop();

  /* ---------- scroll lock (with Lenis) ---------- */
  let lenis = null;
  let locks = 0;
  const lock = () => { if (locks++ === 0) { lenis?.stop(); root.style.overflow = 'hidden'; } };
  const unlock = () => { if (--locks <= 0) { locks = 0; lenis?.start(); root.style.overflow = ''; } };

  /* ---------- floating WhatsApp button ----------
     Always there in the bottom corner, from the top of the page to the foot, through the
     films and the scroll scenes, so help is one tap away wherever you are. It only steps
     aside while you type (the phone's keyboard would push it over the field), and lifts
     above the product page's sticky add-to-cart bar where the two would meet (commerce.css).
     Over the full-screen scenes marked [data-fab-tuck] (the film, the story) it keeps to its
     round icon, in the room their foot rows leave for it (main.css). */
  function initFab() {
    const fab = $('.fab'); if (!fab) return;
    let typing = false;
    const sync = () => fab.classList.toggle('is-hidden', typing);
    // reading on down the page, the label tucks into the icon; scrolling back up brings it out again
    let last = scrollY, from = scrollY;
    const scenes = new Set();
    const tuck = () => {
      const y = scrollY;
      if (y < last - 4 || fab.classList.contains('is-hidden')) from = y;
      fab.classList.toggle('is-tucked', scenes.size > 0 || y - from > 240);
      last = y;
    };
    addEventListener('scroll', tuck, { passive: true });
    const io = new IntersectionObserver(es => { es.forEach(en => (en.isIntersecting ? scenes.add(en.target) : scenes.delete(en.target))); tuck(); });
    $$('[data-fab-tuck]').forEach(el => io.observe(el));
    const isField = el => el?.matches?.('input:not([type="checkbox"]):not([type="radio"]):not([type="range"]), textarea, select, [contenteditable]');
    document.addEventListener('focusin', e => { typing = isField(e.target); sync(); });
    document.addEventListener('focusout', () => { typing = false; requestAnimationFrame(sync); });
    sync();
  }

  /* ---------- footer link groups ---------- */
  // On phones the four groups collapse to their headings, one open at a time, so the
  // whole footer fits the screen; they slide open and shut like an accordion. From 700px
  // up they're always open and can't be closed. .is-open is the state the group is
  // heading to (the arrow follows it at once); [open] stays on until a close finishes.
  function initFootCols() {
    const cols = $$('.foot-col'); if (!cols.length) return;
    const phone = matchMedia(FOOT_PHONE);
    const sync = () => cols.forEach(d => { d.anim?.cancel(); d.open = !phone.matches; d.classList.toggle('is-open', d.open); });
    phone.addEventListener('change', sync);
    sync(); // Liquid renders the desktop state; choose the initial phone state before measuring.
    const set = (d, open) => {
      const body = $('.foot-col__body', d);
      const from = d.open ? body.getBoundingClientRect().height : 0;   // mid-slide: carry on from where it is
      d.anim?.cancel();
      d.classList.toggle('is-open', open);
      if (open) d.open = true;
      if (reduced) { d.open = open; return; }
      const to = open ? body.scrollHeight : 0;
      body.style.overflow = 'hidden';
      d.anim = body.animate(
        [{ height: `${from}px`, opacity: open ? 0 : 1 }, { height: `${to}px`, opacity: open ? 1 : 0 }],
        { duration: open ? 520 : 420, easing: 'cubic-bezier(.16, 1, .3, 1)' },
      );
      d.anim.onfinish = () => { d.anim = null; body.style.overflow = ''; if (!open) d.open = false; };
    };
    cols.forEach(d => $('summary', d).addEventListener('click', e => {
      e.preventDefault();               // the slide decides when [open] changes
      if (!phone.matches) return;       // from 700px the groups stay open
      const open = !d.classList.contains('is-open');
      set(d, open);
      if (open) cols.forEach(o => { if (o !== d && o.classList.contains('is-open')) set(o, false); });
    }));
  }

  /* ---------- copy the email address (footer) ---------- */
  async function copyText(text) {
    try { await navigator.clipboard.writeText(text); return true; } catch {}
    // some in-app browsers block the clipboard API: the old way still works there
    const back = document.activeElement, t = document.createElement('textarea');
    t.value = text; t.readOnly = true; t.style.cssText = 'position:fixed;top:0;left:0;opacity:0;pointer-events:none';
    document.body.appendChild(t); t.select();
    let ok = false; try { ok = document.execCommand('copy'); } catch {}
    t.remove(); back?.focus?.({ preventScroll: true });
    return ok;
  }
  function initCopy() {
    document.addEventListener('click', async e => {
      const b = e.target.closest('[data-copy]'); if (!b) return;
      if (!await copyText(b.dataset.copy)) return;
      b.classList.add('is-done');
      toast(`Copied ${esc(b.dataset.copy)}`);
      clearTimeout(b.doneTimer); b.doneTimer = setTimeout(() => b.classList.remove('is-done'), 1800);
    });
  }

  /* ---------- the footer's links come out like stars at dusk ----------
     The first time the link groups scroll into view they fade up one by one, in a scattered order
     (the same on every visit), as the sky's stars do below them. With a mouse only: on a phone
     nothing on the page waits to appear. */
  function initFootDusk() {
    const foot = $('.site-footer'), cols = $('.foot-cols', foot || document);
    if (!foot || !cols || reduced || !fine) return;
    $$('.foot-col li, .foot-social', foot).forEach((el, i) => el.style.setProperty('--dusk', `${(i * 7 + 3) % 11 * 45}ms`));
    foot.classList.add('is-dusk');
    new IntersectionObserver(([e], io) => { if (!e.isIntersecting) return; foot.classList.add('is-night'); io.disconnect(); }, { threshold: .3 }).observe(cols);
  }

  /* ---------- the footer fits the screen (touch phones) ----------
     At the end of the page the footer fills the window, signup to payment marks, with no strip
     of the section before, however the browser's bars slide in and out.
       • Its content is fitted, once per width, to the height that's always visible (the window
         with the bars out): the spacing scales between roomy and compact (--fit, main.css), the
         wordmark takes any room to spare, up to the full width, and on the shortest screens it
         steps aside (.is-tight).
       • The footer itself is as tall as the window at its tallest (--foot-slack makes up the
         difference), so the window never reaches past its top. main.css pins the signup and link
         groups to the top of the window, so while the bars are out that extra height scrolls by
         beneath them instead of hiding anything.
     The window is at its tallest once the browser's bars tuck away, which they do on your first
     scroll down; until then the footer is taken to need the whole screen. A lower height is only
     ever applied while the footer is out of sight, so nothing shifts in front of you, and nothing
     is re-measured as you scroll, as the bars move or as a link group opens. */
  function initFootFit() {
    const foot = $('.site-footer'); if (!foot || !touch) return;
    const phone = matchMedia(FOOT_PHONE);
    let width = 0, content = 0, screenH = 0, tallest = 0, inView = false;
    let opened = innerHeight, seen = 0;   // the window when the page opened; its height with the bars tucked away, once seen
    // the footer's height as laid out without the slack, less any open group's links
    const contentH = () => foot.offsetHeight - $$('.foot-col.is-open .foot-col__body', foot).reduce((h, b) => h + b.offsetHeight, 0);
    const setHeight = h => { tallest = h; foot.style.setProperty('--foot-slack', `${Math.max(0, Math.ceil(h - content) + 2)}px`); };
    // a taller window takes effect at once (the window must never reach past the footer's top),
    // a shorter one once the footer is out of sight
    const settle = () => {
      const h = seen || screenH;
      if (content && (h > tallest || !inView)) setHeight(h);
    };
    const fit = () => {
      width = innerWidth;
      for (const p of ['--fit', '--mark-w', '--foot-slack']) foot.style.removeProperty(p);
      foot.classList.remove('is-tight');
      content = 0;
      if (!phone.matches) return;
      // the screen's height as held now (iOS reports it upright whichever way the phone is turned)
      const long = Math.max(screen.width, screen.height), short = Math.min(screen.width, screen.height);
      screenH = Math.max(innerHeight, innerWidth > innerHeight ? short : long);
      // always visible: the window as it is now, or the screen less the most the browser's bars
      // can take (status bar, about 59px with a notch or island and 20px without, and both
      // toolbars out), whichever is smaller; a page can open with the bars already tucked away
      const bars = (long >= 780 ? 59 : 20) + 137;
      const room = Math.min(innerHeight, screenH - bars >= 400 ? screenH - bars : innerHeight);
      if (contentH() > room) {
        foot.style.setProperty('--fit', '0');
        if (contentH() > room) foot.classList.add('is-tight');
        else {
          let lo = 0, hi = 1;   // the roomiest spacing that still fits
          for (let i = 0; i < 7; i++) { const mid = (lo + hi) / 2; foot.style.setProperty('--fit', mid.toFixed(3)); if (contentH() > room) hi = mid; else lo = mid; }
          foot.style.setProperty('--fit', lo.toFixed(3));
        }
      } else {
        // room to spare: the wordmark grows into it (max-width: 100% caps it at the full width)
        const mark = $('.foot-mark', foot), r = mark?.getBoundingClientRect();
        if (r?.height) foot.style.setProperty('--mark-w', `${Math.floor((r.height + room - contentH()) * r.width / r.height)}px`);
      }
      content = contentH();
      setHeight(seen || screenH);
    };
    fit();
    document.fonts?.ready.then(fit);   // once more, with the real fonts
    new IntersectionObserver(([e]) => { inView = e.isIntersecting; if (!inView) settle(); }).observe(foot);
    addEventListener('resize', () => {
      if (innerWidth !== width) { opened = innerHeight; seen = 0; return fit(); }   // turned: start afresh
      // taller than when the page opened: the bars have tucked away (the keyboard only ever shortens it)
      if (innerHeight > opened + 40 && innerHeight > seen) { seen = innerHeight; settle(); }
    });
  }

  /* ---------- toasts ---------- */
  function toast(html, action) {
    const wrap = $('[data-toasts]'); if (!wrap) return;
    const el = document.createElement('div');
    el.className = 'toast';
    el.innerHTML = `${icon('check-circle')}<span>${html}</span>${action ? `<a href="${action[1]}" ${action[2] ? `data-${action[2]}` : ''}>${action[0]}</a>` : ''}`;
    wrap.appendChild(el);
    setTimeout(() => { el.classList.add('is-out'); setTimeout(() => el.remove(), 500); }, 3600);
  }

  /* ---------- cart drawer ---------- */
  function bumpBadge() { $$('[data-cart-count]').forEach(b => { b.classList.remove('is-bump'); void b.offsetWidth; b.classList.add('is-bump'); }); }
  // The sockets' and switches' quantity prices (D.volume) in the cart, for one way of buying: how many
  // there are, the tier reached, and the next one
  function volumeState(mode) {
    const v = D.volume; if (!v) return null;
    const n = Cart.items.filter(l => l.variant === mode && v.handles.includes(l.handle)).reduce((a, l) => a + l.qty, 0);
    return n ? { n, tier: v.tiers.filter(t => n >= t.min).pop(), next: v.tiers.find(t => n < t.min), quote: n >= v.quote } : null;
  }
  // a line's price: what Shopify charges after its discounts, with the full price struck through, and
  // under the name what the discount made of it
  function cartLinePriceHTML(l, unit, compare) {
    const now = l.finalTotal ?? unit * l.qty, full = Math.max(compare || 0, unit) * l.qty;
    return `<div class="cart-line__price">${fmt(now)}${full > now ? `<s>${fmt(full)}</s>` : ''}</div>`;
  }
  function cartLineDealHTML(l) {
    const off = (l.lineDiscounts || []).reduce((a, d) => a + d.amount, 0); if (!off) return '';
    const tier = D.volume?.handles.includes(l.handle) && volumeState(l.variant)?.tier;
    return `<div class="cart-line__deal">${icon('tag')}<span>${tier ? `${fmt(l.finalTotal / l.qty)} each, the ${tier.min}+ price` : `${esc(l.lineDiscounts[0].title)} · − ${fmt(off)}`}</span></div>`;
  }
  // where the cart stands on the quantity prices, with a bar to the next one
  function volumeNoteHTML() {
    const v = D.volume; if (!v || !window.AURA_SHOPIFY) return '';   // the demo cart has no discounts
    return ['pro', 'diy'].map(mode => {
      const s = volumeState(mode); if (!s) return '';
      const what = `${s.n} switch${s.n > 1 ? 'es' : ''} &amp; sockets${mode === 'pro' ? ', fitted' : ', supply only'}`;
      const offAt = t => fmt(t.off[mode]);
      const text = s.quote ? `<b>${what}</b>: for ${v.quote} or more, <a class="text-link" href="${route('contact.html#quote')}">ask us for a quote</a>.`
        : `<b>${what}${s.tier ? `: ${offAt(s.tier)} off each` : ''}</b>${s.next ? ` · add ${s.next.min - s.n} more for ${offAt(s.next)} off each` : ''}`;
      const from = s.tier?.min || 0, to = s.next?.min || v.quote;
      return `<div class="cart-vol">${icon('tag')}<div><span>${text}</span>${s.next ? `<span class="cart-vol__bar" aria-hidden="true"><i style="width:${Math.round((s.n - from) / (to - from) * 100)}%"></i></span>` : ''}</div></div>`;
    }).join('');
  }
  function cartLineHTML(l) {
    const p = D.byHandle(l.handle); if (!p) return '';
    const unit = Cart.priceOf(l);
    const compare = l.compare ?? p.compare;
    const img = l.image || (p.colors && l.color && p.colors.find(c => c.name === l.color)?.image) || p.images[0];
    const href = route(p.url || p.page || `product.html?p=${p.handle}`);
    return `
    <div class="cart-line" data-key="${esc(l.key)}">
      <a class="cart-line__media" href="${route(href)}" style="--tint:${p.tint}"><img src="${img}" alt="${esc(p.name)}"></a>
      <div>
        <div class="cart-line__top">
          <div><a class="cart-line__name" href="${route(href)}">${esc(p.name)}</a>
            <div class="cart-line__meta"><span>${window.AURA_SHOPIFY && p.handle === 'guard-2' ? (l.variant === 'diy' ? icon('box') + ' Delivered' : icon('tools') + ' Measured and fitted') : (l.variant === 'diy' ? icon('box') + ' DIY · Delivered' : icon('tools') + ' Pro installation')}</span>${l.color ? `<span>${esc(l.color)}</span>` : ''}</div>${cartLineDealHTML(l)}</div>
          ${cartLinePriceHTML(l, unit, compare)}
        </div>
        ${Object.entries(l.properties || {}).filter(([key]) => !key.startsWith('_')).map(([key, value]) => `<div class="cart-line__meta">${esc(key)}: ${esc(value)}</div>`).join('')}
        <div class="cart-line__bottom">
          <div class="qty"><button type="button" data-qty="-1" aria-label="Decrease quantity">${icon('minus')}</button><output aria-live="polite">${l.qty}</output><button type="button" data-qty="1" aria-label="Increase quantity">${icon('plus')}</button></div>
          <button class="text-btn" type="button" data-remove>Remove</button>
        </div>
      </div>
    </div>`;
  }
  function renderCart() {
    const n = Cart.count();
    $$('[data-cart-count]').forEach(b => { b.textContent = n; b.classList.toggle('is-on', n > 0); });
    $$('[data-cart-count-text]').forEach(b => (b.textContent = n ? `${n} item${n > 1 ? 's' : ''}` : ''));
    const body = $('[data-cart-body]'), foot = $('[data-cart-foot]'); if (!body) return;
    if (Cart.loading) { body.innerHTML = '<div class="cart-empty" role="status"><p class="muted">Loading your cart…</p></div>'; foot.style.display = 'none'; return; }
    if (!n) {
      body.innerHTML = `<div class="cart-empty"><div class="orb">${icon('bag')}</div><h3 class="h4">Your cart is empty</h3><p class="muted small mw-30">Browse the devices, or ask us to recommend a setup for your home.</p><div class="btn-row justify-center mt-6"><a class="btn btn--sm" href="${route(`shop.html`)}">Shop all</a><a class="btn btn--sm btn--ghost" href="${route(`contact.html#quote`)}">Free quote</a></div></div>`;
      foot.innerHTML = ''; foot.style.display = 'none'; return;
    }
    foot.style.display = '';
    const inCart = new Set(Cart.items.map(i => i.handle));
    const ups = ['floodlight-camera', 'indoorcam-p1', 'smart-lock', 'solar-camera-pro'].filter(h => !inCart.has(h)).slice(0, 2).map(D.byHandle).filter(Boolean);
    body.innerHTML = Cart.items.map(cartLineHTML).join('') + (ups.length ? `
      <div class="cart-upsell"><h3>Complete your system</h3>${ups.map(p => `
        <div class="mini-card"><a class="media" href="${route(`product.html?p=${p.handle}`)}" style="--tint:${p.tint}"><img src="${p.images[0]}" alt=""></a><div><b>${p.name}</b><span>${fmt(p.from)}</span></div><button class="icon-btn icon-btn--solid" type="button" data-add="${p.handle}" data-variant="diy" aria-label="Add ${esc(p.name)}">${icon('plus')}</button></div>`).join('')}</div>` : '');
    // discounts: only the Shopify cart has them (the bundle discount), one row each
    const sub = Cart.subtotal(), discounts = Cart.discounts || [], savings = Math.round((Cart.compareTotal() - sub - discounts.reduce((a, d) => a + d.amount, 0)) * 100) / 100;
    foot.innerHTML = `
      ${volumeNoteHTML()}
      ${Cart.hasPro() ? `<div class="cart-note">${icon('calendar')}<span>Pro installation included. We’ll call to book a time that suits you after checkout.</span></div>` : ''}
      ${savings > 0 ? `<div class="cart-row cart-row--save"><span>You’re saving</span><b>${fmt(savings)}</b></div>` : ''}
      ${discounts.map(d => `<div class="cart-row cart-row--save"><span>${esc(d.title)}</span><b>− ${fmt(d.amount)}</b></div>`).join('')}
      <div class="cart-row cart-row--total"><span>Subtotal</span><b>${fmt(sub)}</b></div>
      <a class="btn btn--blue btn--block btn--lg" href="${route(window.AURA_SHOPIFY ? 'cart.html' : 'checkout.html')}">${window.AURA_SHOPIFY ? 'Review cart & checkout' : 'Continue to demo checkout'}</a>
      <div class="flex items-center gap-12 justify-between"><a class="text-btn" href="${route(`cart.html`)}">View full cart</a><span class="tiny muted">No monthly fees · ${D.site.warranty} warranty</span></div>`;
    hydrateButtons(foot);
  }
  function initCart() {
    const drawer = $('#cartDrawer'), scrim = $('[data-scrim]'); if (!drawer) return;
    let lastFocus;
    const open = () => { lastFocus = document.activeElement; drawer.classList.add('is-open'); drawer.setAttribute('aria-hidden', 'false'); scrim.classList.add('is-on'); lock(); setTimeout(() => $('[data-cart-close]', drawer).focus(), 50); };
    const close = () => { if (!drawer.classList.contains('is-open')) return; drawer.classList.remove('is-open'); drawer.setAttribute('aria-hidden', 'true'); scrim.classList.remove('is-on'); unlock(); lastFocus?.focus?.(); };
    A.openCart = open; A.closeCart = close;
    document.addEventListener('click', async e => {
      if (e.target.closest('[data-cart-open]')) { e.preventDefault(); open(); }
      if (e.target.closest('[data-cart-close]') || e.target === scrim) close();
      const line = e.target.closest('.cart-line');
      if (line) {
        const k = line.dataset.key, it = Cart.items.find(i => i.key === k);
        const q = e.target.closest('[data-qty]'); if (q && it) Cart.set(k, it.qty + Number(q.dataset.qty));
        if (e.target.closest('[data-remove]')) { line.style.transition = 'opacity .3s, transform .4s'; line.style.opacity = 0; line.style.transform = 'translateX(20px)'; setTimeout(() => Cart.remove(k), 250); }
      }
      const add = e.target.closest('[data-add]');
      if (add) { e.preventDefault(); const ok = await Cart.add(add.dataset.add, add.dataset.variant || 'diy', add.dataset.color || null); if (ok === false) return; const p = D.byHandle(add.dataset.add); if (drawer.classList.contains('is-open')) toast(`${esc(p.name)} added`); else { toast(`<b>${esc(p.name)}</b> added to cart`, ['View cart', '#', 'cart-open']); } }
    });
    document.addEventListener('keydown', e => { if (e.key === 'Escape') close(); });
    document.addEventListener('cart:change', renderCart);
    renderCart();
  }

  /* ---------- quick add modal ---------- */
  function initQuick() {
    const modal = $('#quickModal'); if (!modal) return;
    const scrim = $('[data-scrim]');
    let last;
    const close = () => { if (!modal.classList.contains('is-open')) return; modal.classList.remove('is-open'); modal.setAttribute('aria-hidden', 'true'); scrim.classList.remove('is-on'); unlock(); last?.focus?.(); };
    const open = h => {
      const p = D.byHandle(h); if (!p) return;
      last = document.activeElement;
      let variant = 'diy', color = p.colors ? p.colors[0].name : null;
      $('[data-modal-body]', modal).innerHTML = `
        <div class="qa">
          <div class="qa__media" style="--tint:${p.tint}"><img data-qa-img src="${p.images[0]}" alt="${esc(p.name)}"></div>
          <div class="qa__body">
            <div><h2 class="h3">${esc(p.name)}</h2><p class="muted mt-8">${esc(p.tagline)}</p></div>
            ${p.colors ? `<div><p class="qa__label">Finish: <span data-qa-color>${color}</span></p>${A.swatchesHTML(p.colors, color, 'data-qa-swatch')}</div>` : ''}
            <div class="opt-cards" role="radiogroup" aria-label="Installation">${optCard(p, 'diy', true)}${optCard(p, 'pro', false)}</div>
            <button class="btn btn--blue btn--lg btn--block" type="button" data-qa-add>Add to cart · <span data-qa-price>${fmt(p.price[1])}</span></button>
            <a class="link small" href="${route(`product.html?p=${p.handle}`)}">View product details ${icon('arrow')}</a>
          </div>
        </div>`;
      const body = $('[data-modal-body]', modal);
      hydrateButtons(body);
      body.onclick = async e => {
        const o = e.target.closest('[data-opt]');
        if (o) { variant = o.dataset.opt; $$('[data-opt]', body).forEach(x => x.setAttribute('aria-checked', x === o)); $('[data-qa-price]', body).textContent = fmt(p.price[variant === 'diy' ? 1 : 0]); }
        const s = e.target.closest('[data-qa-swatch]');
        if (s) { color = s.dataset.qaSwatch; $$('[data-qa-swatch]', body).forEach(x => x.setAttribute('aria-pressed', x === s)); $('[data-qa-color]', body).textContent = color; $('[data-qa-img]', body).src = p.colors.find(c => c.name === color).image; }
        if (e.target.closest('[data-qa-add]')) { const ok = await Cart.add(p.handle, variant, color); if (ok === false) return; close(); setTimeout(() => A.openCart(), 250); }
      };
      modal.classList.add('is-open'); modal.setAttribute('aria-hidden', 'false'); scrim.classList.add('is-on'); lock();
      setTimeout(() => $('[data-modal-close]', modal).focus(), 60);
    };
    A.quickAdd = open;
    document.addEventListener('click', e => {
      const q = e.target.closest('[data-quick]'); if (q) { e.preventDefault(); open(q.dataset.quick); }
      if (e.target.closest('[data-modal-close]') || (modal.classList.contains('is-open') && e.target === modal)) close();
    });
    scrim.addEventListener('click', close);
    document.addEventListener('keydown', e => { if (e.key === 'Escape') close(); });
  }
  // finish swatches; finishes with a material are grouped under it, and an aluminium frame's
  // swatch shows its black centre (ring)
  function swatchesHTML(colors, selected, attr) {
    const btn = c => `<button type="button" class="swatch${c.ring ? ' swatch--ring' : ''}${c.material ? ` swatch--${c.material.toLowerCase()}` : ''}" style="--c:${c.hex}${c.ring ? `;--ring:${c.ring}` : ''}" aria-pressed="${c.name === selected}" ${attr}="${esc(c.name)}" aria-label="${esc(c.name)}"></button>`;
    const groups = [...new Set(colors.map(c => c.material).filter(Boolean))];
    if (!groups.length) return `<div class="swatches">${colors.map(btn).join('')}</div>`;
    return `<div class="swatch-groups">${groups.map(g => `<div class="swatch-group"><span class="swatch-group__name">${esc(g)}</span><div class="swatches">${colors.filter(c => c.material === g).map(btn).join('')}</div></div>`).join('')}</div>`;
  }
  // The finish's photo in a small card over its swatch while the mouse rests on it. Along the row the
  // card glides to the next swatch and its photo crossfades; it goes once the pointer leaves the swatches.
  // (sel: what counts as a swatch, each naming its finish in data-color; srcFor(name, swatch): its photo)
  function swatchPeek(root, srcFor, tint, sel = '.swatch[data-color]') {
    if (!fine || !root) return;
    let card, hideT, on = null;
    const make = () => {
      card = document.createElement('div'); card.className = 'swatch-peek'; card.setAttribute('aria-hidden', 'true');
      if (tint) card.style.setProperty('--tint', tint);
      card.innerHTML = '<span class="swatch-peek__media"><img alt=""><img alt=""></span><span class="swatch-peek__name"></span>';
      document.body.append(card);
    };
    const show = sw => {
      const name = sw.dataset.color, src = srcFor(name, sw); if (!src) return;
      if (!card) make();
      clearTimeout(hideT);
      const [a, b] = $$('img', card), cur = a.classList.contains('is-on') ? a : b, next = cur === a ? b : a;
      if (cur.getAttribute('src') !== src) { next.src = src; next.classList.add('is-on'); cur.classList.remove('is-on'); }
      const [tone, ...rest] = name.split(' ');
      $('.swatch-peek__name', card).innerHTML = `${esc(tone)} <small>${esc(rest.join(' '))}</small>`;
      const r = sw.getBoundingClientRect();
      card.style.setProperty('--x', `${r.left + r.width / 2}px`); card.style.setProperty('--y', `${r.top}px`);
      if (!on) { card.classList.add('is-snap'); card.offsetWidth; card.classList.remove('is-snap'); }   // appears in place, glides only between swatches
      card.classList.add('is-on'); on = sw;
    };
    // while the page scrolls (a smooth scroll runs on after the wheel stops) it stays on its swatch
    const follow = () => {
      if (!on) return;
      if (!on.isConnected) { card.classList.remove('is-on'); on = null; return; }
      const r = on.getBoundingClientRect();
      card.classList.add('is-snap');
      card.style.setProperty('--x', `${r.left + r.width / 2}px`); card.style.setProperty('--y', `${r.top}px`);
      card.offsetWidth; card.classList.remove('is-snap');
    };
    const hide = (wait = 140) => { clearTimeout(hideT); hideT = setTimeout(() => { card?.classList.remove('is-on'); on = null; }, wait); };
    root.addEventListener('pointerover', e => { const sw = e.target.closest?.(sel); if (sw && e.pointerType === 'mouse') show(sw); });
    root.addEventListener('pointerout', e => { const sw = e.target.closest?.(sel); if (sw && !sw.contains(e.relatedTarget)) hide(); });
    addEventListener('scroll', follow, { passive: true });
  }
  // a card's dots: one per colour, so "White glass" and "White aluminium" share a dot
  const cardColors = colors => colors.filter((c, i) => colors.findIndex(x => x.name.split(' ')[0] === c.name.split(' ')[0]) === i);

  function optCard(p, v, checked) {
    const pro = v === 'pro';
    return `<button type="button" class="opt" role="radio" aria-checked="${checked}" data-opt="${v}">
      <span class="opt__radio"></span>
      <span class="opt__main"><b>${pro ? 'Pro installation' : 'DIY installation'}</b><span>${pro ? 'Fitted, tested &amp; explained by a licensed installer' : 'Delivered to your door, ready to set up'}</span></span>
      <span class="opt__price">${fmt(p.price[pro ? 0 : 1])}</span>
    </button>`;
  }

  /* ---------- declarative icons: <span data-icon="lock"></span>, <span data-stars></span> ---------- */
  function hydrateIcons(scope = document) {
    $$('[data-icon]', scope).forEach(el => { if (!el.firstElementChild) el.innerHTML = icon(el.dataset.icon); });
    $$('[data-stars]', scope).forEach(el => { if (!el.firstElementChild) el.outerHTML = stars(); });
  }

  /* ---------- custom select: every <select> becomes a branded, accessible listbox ---------- */
  let cselectId = 0;
  function enhanceSelects(scope = document) {
    $$('select:not([multiple]):not([data-native]):not(.cselect__native)', scope).forEach(sel => {
      const id = ++cselectId;
      const wrap = document.createElement('div');
      wrap.className = 'cselect' + (sel.classList.contains('select--sm') ? ' cselect--sm' : '');
      sel.parentNode.insertBefore(wrap, sel);
      wrap.appendChild(sel);
      sel.classList.add('cselect__native'); sel.tabIndex = -1; sel.setAttribute('aria-hidden', 'true');
      const btnId = sel.id ? sel.id + '-btn' : `cselect-${id}`;
      const label = sel.id && document.querySelector(`label[for="${sel.id}"]`);
      if (label) { label.id = label.id || `${btnId}-label`; label.htmlFor = btnId; }
      const aria = label ? label.id : null;
      wrap.insertAdjacentHTML('beforeend', `<button type="button" class="cselect__btn" id="${btnId}" aria-haspopup="listbox" aria-expanded="false" ${aria ? `aria-labelledby="${aria} ${btnId}"` : `aria-label="${esc(sel.getAttribute('aria-label') || 'Select')}"`}><span class="cselect__value"></span><span class="cselect__chev">${icon('chevron')}</span></button><ul class="cselect__list" role="listbox" id="${btnId}-list" tabindex="-1" ${aria ? `aria-labelledby="${aria}"` : ''}></ul>`);
      const btn = $('.cselect__btn', wrap), list = $('.cselect__list', wrap), val = $('.cselect__value', wrap);
      let active = -1, typed = '', typedT;
      const opts = () => [...sel.options];
      const render = () => {
        list.innerHTML = opts().map((o, i) => `<li class="cselect__opt" role="option" id="${btnId}-o${i}" data-i="${i}" aria-selected="${o.selected}" ${o.disabled ? 'aria-disabled="true"' : ''}>${esc(o.textContent)}${icon('check')}</li>`).join('');
        const cur = sel.options[sel.selectedIndex];
        val.textContent = cur ? cur.textContent : '';
        val.classList.toggle('is-placeholder', !cur || cur.value === '');
      };
      const setActive = i => {
        const items = $$('.cselect__opt', list); if (!items.length) return;
        active = Math.max(0, Math.min(items.length - 1, i));
        items.forEach((li, k) => li.classList.toggle('is-active', k === active));
        list.setAttribute('aria-activedescendant', items[active].id);
        const li = items[active], top = li.offsetTop, bot = top + li.offsetHeight;
        if (top < list.scrollTop) list.scrollTop = top - 6; else if (bot > list.scrollTop + list.clientHeight) list.scrollTop = bot - list.clientHeight + 6;
      };
      const open = () => {
        if (wrap.classList.contains('is-open')) return;
        $$('.cselect.is-open').forEach(o => o !== wrap && o.dispatchEvent(new Event('cselect:close')));
        render();
        const r = btn.getBoundingClientRect();
        wrap.classList.toggle('is-up', innerHeight - r.bottom < 320 && r.top > innerHeight - r.bottom);
        wrap.classList.add('is-open'); btn.setAttribute('aria-expanded', 'true');
        setActive(sel.selectedIndex < 0 ? 0 : sel.selectedIndex);
        list.focus({ preventScroll: true });
      };
      const close = (focusBtn = true) => {
        if (!wrap.classList.contains('is-open')) return;
        wrap.classList.remove('is-open'); btn.setAttribute('aria-expanded', 'false');
        if (focusBtn) btn.focus({ preventScroll: true });
      };
      const choose = i => {
        const o = sel.options[i]; if (!o || o.disabled) return;
        if (sel.selectedIndex !== i) { sel.selectedIndex = i; sel.dispatchEvent(new Event('input', { bubbles: true })); sel.dispatchEvent(new Event('change', { bubbles: true })); }
        render(); close();
      };
      wrap.addEventListener('cselect:close', () => close(false));
      btn.addEventListener('click', () => (wrap.classList.contains('is-open') ? close() : open()));
      btn.addEventListener('keydown', e => { if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) { e.preventDefault(); open(); } });
      list.addEventListener('click', e => { const li = e.target.closest('.cselect__opt'); if (li) choose(+li.dataset.i); });
      list.addEventListener('mousemove', e => { const li = e.target.closest('.cselect__opt'); if (li && +li.dataset.i !== active) setActive(+li.dataset.i); });
      list.addEventListener('keydown', e => {
        const n = sel.options.length;
        if (e.key === 'ArrowDown') { e.preventDefault(); setActive(active + 1); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(active - 1); }
        else if (e.key === 'Home') { e.preventDefault(); setActive(0); }
        else if (e.key === 'End') { e.preventDefault(); setActive(n - 1); }
        else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); choose(active); }
        else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(); }
        else if (e.key === 'Tab') close(false);
        else if (e.key.length === 1) {
          typed += e.key.toLowerCase(); clearTimeout(typedT); typedT = setTimeout(() => (typed = ''), 600);
          const i = opts().findIndex(o => o.textContent.trim().toLowerCase().startsWith(typed)); if (i > -1) setActive(i);
        }
      });
      document.addEventListener('pointerdown', e => { if (!wrap.contains(e.target)) close(false); });
      sel.addEventListener('change', render);
      if (sel.required) btn.setAttribute('aria-required', 'true');
      render();
    });
  }

  /* ---------- press feedback on touch screens ----------
     A phone sets :active the moment a thumb lands, including the start of a scroll, so
     buttons and cards styled with :active would flash and shrink under every swipe. Here
     a press shows only for a deliberate tap: after a short hold without moving, or briefly
     on release. It clears as soon as the browser takes the touch for a scroll
     (pointercancel) or the finger drifts. main.css styles .is-pressed. */
  const PRESSABLE = '.btn, .pcard__add, .pcard, .icard, .cat-chip, .co-cm, .qcard, .svc-way, .co-link, a.co-place, .qlink, .help-card__links a, .jcard, .jr-feature, .mnav__cat, .swatch';
  function initPress() {
    if (!touch) return;
    let el = null, timer = 0, x0 = 0, y0 = 0;
    const drop = () => { clearTimeout(timer); el?.classList.remove('is-pressed'); el = null; };
    document.addEventListener('pointerdown', e => {
      drop();
      if (e.pointerType === 'mouse') return;
      el = e.target.closest(PRESSABLE); if (!el) return;
      x0 = e.clientX; y0 = e.clientY;
      timer = setTimeout(() => el?.classList.add('is-pressed'), 90);
    }, { passive: true });
    document.addEventListener('pointermove', e => { if (el && Math.hypot(e.clientX - x0, e.clientY - y0) > 10) drop(); }, { passive: true });
    document.addEventListener('pointercancel', drop, { passive: true });
    addEventListener('scroll', () => { if (el) drop(); }, { passive: true });
    document.addEventListener('pointerup', () => {
      if (!el) return;
      const t = el; clearTimeout(timer); el = null;
      t.classList.add('is-pressed'); setTimeout(() => t.classList.remove('is-pressed'), 160);   // a quick tap still answers
    }, { passive: true });
  }

  /* ---------- rails: tab and chip rows that scroll sideways on small screens ---------- */
  // Brings an item to the middle of its own sideways scroller, without moving the page
  function centerInRail(el, smooth = true) {
    let rail = el?.parentElement;
    for (; rail && rail !== document.body; rail = rail.parentElement) {
      if (/(auto|scroll)/.test(getComputedStyle(rail).overflowX) && rail.scrollWidth > rail.clientWidth + 1) break;
    }
    if (!rail || rail === document.body) return;
    const r = rail.getBoundingClientRect(), b = el.getBoundingClientRect();
    rail.scrollTo({ left: rail.scrollLeft + (b.left - r.left) - (r.width - b.width) / 2, behavior: smooth && !reduced ? 'smooth' : 'auto' });
  }
  function initRails() {
    // open on the chosen tab or chip, even when it's far along the row
    $$('[role="tablist"], .chip-rail, .subnav__links').forEach(rail => {
      const cur = $('[aria-selected="true"], [aria-current="page"], [aria-current="true"], [aria-pressed="true"], .is-active', rail);
      if (cur) centerInRail(cur, false);
    });
    document.addEventListener('click', e => { const t = e.target.closest('[role="tab"], .chip-rail > *'); if (t) centerInRail(t); });
    // arrow keys, Home and End move along a tab list (WAI-ARIA tabs pattern)
    document.addEventListener('keydown', e => {
      const tab = e.target.closest?.('[role="tab"]');
      if (!tab || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return;
      const tabs = $$('[role="tab"]', tab.closest('[role="tablist"]')).filter(t => !t.disabled && t.offsetParent);
      const i = tabs.indexOf(tab), n = tabs.length;
      const j = e.key === 'Home' ? 0 : e.key === 'End' ? n - 1 : (i + (e.key === 'ArrowRight' ? 1 : -1) + n) % n;
      e.preventDefault(); tabs[j].focus(); tabs[j].click();
    });
  }

  /* ---------- branded page scroll rail (replaces the OS scrollbar) ---------- */
  function initRail() {
    if (!fine) return;
    const rail = document.createElement('div');
    rail.className = 'rail'; rail.setAttribute('aria-hidden', 'true');
    rail.innerHTML = '<div class="rail__thumb"></div>';
    document.body.appendChild(rail);
    const thumb = rail.firstChild;
    let idle, drag = null, m = null, queued = 0;
    const metrics = () => {
      const docH = document.documentElement.scrollHeight, vh = innerHeight, railH = rail.clientHeight;
      const th = Math.max(40, railH * vh / docH);
      return { docH, vh, railH, th, max: docH - vh };
    };
    // drawn once a frame at most, after the scroll's other handlers have moved things: the page's
    // height is measured only when it changes, and what's under the rail is looked up once
    const paint = () => {
      queued = 0;
      if (!m || rail.style.display === 'none') { rail.style.display = ''; m = metrics(); }
      if (m.max <= 0) { rail.style.display = 'none'; return; }
      thumb.style.height = m.th + 'px';
      thumb.style.transform = `translateY(${(scrollY / m.max) * (m.railH - m.th)}px)`;
      const under = document.elementFromPoint(innerWidth - 8, 60);
      rail.classList.toggle('on-night', !!under?.closest('.story.is-night, .bg-night, .site-footer, .on-dark, .banner'));
    };
    const schedule = () => { if (!queued) queued = requestAnimationFrame(paint); };
    const remeasure = () => { m = null; schedule(); };
    const wake = () => { rail.classList.add('is-on'); clearTimeout(idle); idle = setTimeout(() => rail.classList.remove('is-on'), 1100); };
    const scrollTo = y => (lenis ? lenis.scrollTo(y, { immediate: true, force: true }) : window.scrollTo(0, y));
    addEventListener('scroll', () => { schedule(); wake(); }, { passive: true });
    addEventListener('resize', remeasure);
    new ResizeObserver(remeasure).observe(document.body);
    thumb.addEventListener('pointerdown', e => { e.preventDefault(); drag = { y: e.clientY, s: scrollY }; rail.classList.add('is-drag'); root.classList.add('is-dragging-rail'); thumb.setPointerCapture(e.pointerId); });
    thumb.addEventListener('pointermove', e => { if (!drag) return; const m = metrics(); scrollTo(drag.s + (e.clientY - drag.y) * (m.max / (m.railH - m.th))); });
    const end = () => { drag = null; rail.classList.remove('is-drag'); root.classList.remove('is-dragging-rail'); wake(); };
    thumb.addEventListener('pointerup', end); thumb.addEventListener('pointercancel', end);
    rail.addEventListener('pointerdown', e => { if (e.target !== rail) return; const m = metrics(), r = rail.getBoundingClientRect(); const k = (e.clientY - r.top - m.th / 2) / (m.railH - m.th); lenis ? lenis.scrollTo(Math.max(0, Math.min(1, k)) * m.max) : scrollTo(k * m.max); });
    schedule();
  }

  /* ---------- buttons: rolling label + arrow ---------- */
  function hydrateButtons(scope = document) {
    $$('.btn:not([data-hydrated])', scope).forEach(b => {
      b.dataset.hydrated = '1';
      if (b.querySelector('.btn__label')) return;
      const text = b.textContent.trim();
      const isIco = n => n.nodeType === 1 && (n.matches('svg') || n.matches('[data-icon]'));
      const lead = [...b.childNodes].filter(isIco).map(n => n.outerHTML).join('');
      const html = [...b.childNodes].filter(n => !isIco(n)).map(n => (n.nodeType === 3 ? esc(n.textContent) : n.outerHTML)).join('').trim();
      if (!text) return;
      const arrow = !b.classList.contains('btn--noarrow') && !b.hasAttribute('data-noarrow');
      b.innerHTML = `${lead}<span class="btn__label"><span>${html}</span><span aria-hidden="true">${html}</span></span>${arrow ? `<span class="btn__arrow">${icon('arrow')}</span>` : ''}`;
    });
  }

  /* ---------- motion ---------- */
  // Split by explicit <br>/.line markers when present, otherwise by words → measured lines.
  // Several at once go in three passes (all into words, then every line measured, then all into
  // lines), so the page is laid out once for all of them rather than twice for each
  function splitLines(...els) {
    els = els.filter(el => !el.classList.contains('is-split'));
    const words = els.map(el => {
      try {
        const tmp = document.createElement('div'); tmp.innerHTML = el.innerHTML;
        const tokens = [];
        tmp.childNodes.forEach(n => {
          if (n.nodeType === 3) n.textContent.split(/(\s+)/).forEach(t => t && tokens.push(t));
          else if (n.nodeName === 'BR') tokens.push('\n');
          else tokens.push(n.outerHTML);
        });
        el.innerHTML = tokens.map(t => (t === '\n' ? '<br data-br>' : /^\s+$/.test(t) ? ' ' : `<span class="w" style="display:inline-block">${t}</span>`)).join('');
        return $$('.w, br[data-br]', el);
      } catch { return null; }
    });
    const lines = words.map(ws => {
      if (!ws) return null;
      const lines = []; let top = null, cur = [];
      ws.forEach(w => {
        if (w.tagName === 'BR') { if (cur.length) lines.push(cur); cur = []; top = null; return; }
        const t = w.offsetTop;
        if (top !== null && Math.abs(t - top) > 4) { lines.push(cur); cur = []; }
        top = t; cur.push(w.innerHTML);
      });
      if (cur.length) lines.push(cur);
      return lines;
    });
    els.forEach((el, k) => {
      if (lines[k]) el.innerHTML = lines[k].map((l, i) => `<span class="split-line"><span style="--i:${i}">${l.join(' ')}</span></span>`).join('');
      el.classList.add('is-split');
    });
  }

  function initMotion() {
    splitLines(...$$('[data-split]'));
    // Touch screens reveal as soon as anything shows, so a quick flick never lands on blank space
    const io = new IntersectionObserver(entries => entries.forEach(en => {
      if (en.isIntersecting) { en.target.classList.add('is-in'); io.unobserve(en.target); }
    }), touch ? { rootMargin: '0px 0px 4% 0px', threshold: 0 } : { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    A.observe = scope => $$('[data-reveal]:not(.is-in), [data-split]:not(.is-in), .img-reveal:not(.is-in), [data-count]:not(.is-in)', scope).forEach(el => io.observe(el));
    A.observe(document);

    // counters
    const cio = new IntersectionObserver(entries => entries.forEach(en => {
      if (!en.isIntersecting) return; cio.unobserve(en.target);
      const el = en.target, end = parseFloat(el.dataset.count), dec = (el.dataset.count.split('.')[1] || '').length;
      const pre = el.dataset.prefix || '', suf = el.dataset.suffix || '';
      if (reduced) { el.textContent = pre + end.toLocaleString('en-IE', { minimumFractionDigits: dec }) + suf; return; }
      const t0 = performance.now(), dur = 1800, nf = new Intl.NumberFormat('en-IE', { minimumFractionDigits: dec, maximumFractionDigits: dec });
      const tick = t => { const k = Math.min(1, (t - t0) / dur), e = 1 - Math.pow(1 - k, 4); el.textContent = pre + nf.format(end * e) + suf; if (k < 1) requestAnimationFrame(tick); };
      requestAnimationFrame(tick);
    }), { threshold: .4 });
    $$('[data-count]').forEach(el => cio.observe(el));

    // marquees: duplicate track to fill
    $$('.marquee').forEach(m => {
      const track = $('.marquee__track', m); if (!track || m.dataset.ready) return;
      m.dataset.ready = 1;
      const clone = track.cloneNode(true); clone.setAttribute('aria-hidden', 'true'); $$('a,button', clone).forEach(a => a.tabIndex = -1);
      m.appendChild(clone);
    });

    // parallax + scroll progress
    // Parallax only with a mouse: on a phone the page scrolls on its own thread, and a picture
    // moved by script a frame behind it visibly jitters
    const par = fine ? $$('[data-parallax]') : [];
    const prog = $$('[data-progress]');
    if (!reduced && (par.length || prog.length)) {
      // every position read first, then everything moved, so the page is laid out once, not once per picture
      const update = () => {
        const vh = innerHeight;
        const ks = par.map(el => {
          const r = el.getBoundingClientRect();
          return r.bottom < -200 || r.top > vh + 200 ? null : (r.top + r.height / 2 - vh / 2) / vh;
        });
        const ps = prog.map(el => {
          const r = el.getBoundingClientRect();
          const total = r.height - vh * (parseFloat(el.dataset.progressOffset || 1));
          return Math.min(1, Math.max(0, -r.top / Math.max(1, total)));
        });
        par.forEach((el, i) => { if (ks[i] !== null) el.style.transform = `translate3d(0, ${(ks[i] * parseFloat(el.dataset.parallax || 60)).toFixed(1)}px, 0)`; });
        prog.forEach((el, i) => {
          el.style.setProperty('--p', ps[i].toFixed(4));
          el.dispatchEvent(new CustomEvent('progress', { detail: ps[i] }));
        });
      };
      A.onFrame = update;
      addEventListener('scroll', update, { passive: true }); addEventListener('resize', update); update();
    }

    // magnetic buttons
    if (fine && !reduced) {
      $$('[data-magnetic]').forEach(el => {
        el.addEventListener('mousemove', e => { const r = el.getBoundingClientRect(); el.style.transform = `translate(${(e.clientX - r.left - r.width / 2) * .25}px, ${(e.clientY - r.top - r.height / 2) * .35}px)`; });
        el.addEventListener('mouseleave', () => { el.style.transform = ''; });
      });
    }
  }

  /* ---------- accordions: animated <details> ---------- */
  function initAccordions(scope = document) {
    $$('.acc__item:not([data-acc])', scope).forEach(d => {
      d.dataset.acc = 1;
      const sum = $('summary', d), content = $('.acc__content', d);
      if (!content || reduced) return;
      sum.addEventListener('click', e => {
        e.preventDefault();
        if (d.open) {
          const h = content.offsetHeight; content.style.height = h + 'px';
          requestAnimationFrame(() => { content.style.transition = 'height .5s var(--ease)'; content.style.height = '0px'; });
          content.addEventListener('transitionend', () => { d.open = false; content.style.cssText = ''; }, { once: true });
        } else {
          if (d.closest('[data-acc-single]')) $$('.acc__item[open]', d.closest('[data-acc-single]')).forEach(o => o !== d && $('summary', o).click());
          d.open = true; const h = content.scrollHeight; content.style.height = '0px';
          requestAnimationFrame(() => { content.style.transition = 'height .6s var(--ease)'; content.style.height = h + 'px'; });
          content.addEventListener('transitionend', () => { content.style.cssText = ''; }, { once: true });
        }
      });
    });
  }

  /* ---------- demo forms ---------- */
  const EMAIL = /^\S+@\S+\.\S+$/;
  // shows (or clears) the signup's message under it; true when there's nothing wrong
  const newsCheck = (f, msg) => {
    const err = $('#newsErr'), inp = $('[inputmode="email"]', f);
    f.classList.toggle('is-invalid', !!msg);
    inp?.toggleAttribute('aria-invalid', !!msg);
    if (err) { err.textContent = msg; err.hidden = !msg; }
    return !msg;
  };
  function initForms() {
    // a message under the signup goes as soon as what's typed is an address
    document.addEventListener('input', e => {
      const f = e.target.closest?.('[data-news-form].is-invalid');
      if (f && EMAIL.test(e.target.value.trim())) newsCheck(f, '');
    });
    // The signup checks the address itself and says what's wrong under the field: the field is plain
    // text (with the email keyboard), so the browser never adds its own bubble or hover tooltip. Checked
    // as the submit starts (capture), before Shopify's own bot check on its customer form sees it: a
    // bad address stops here, a good one goes on to Shopify
    document.addEventListener('submit', e => {
      const f = e.target;
      if (!f.matches('[data-news-form]')) return;
      const inp = $('[inputmode="email"]', f), v = inp.value.trim();
      if (!newsCheck(f, v ? (EMAIL.test(v) ? '' : 'Check the address, such as name@example.ie') : 'Enter your email address, such as name@example.ie')) { e.preventDefault(); e.stopImmediatePropagation(); inp.focus(); return; }
      if (f.matches('[data-shopify-news]')) return;
      e.preventDefault(); e.stopImmediatePropagation();
      f.innerHTML = `<p class="news-form__done" role="status">${icon('check-circle')} Demo only. You have not been subscribed. Contact us by email for updates.</p>`;
    }, true);
    document.addEventListener('submit', e => {
      const f = e.target;
      if (f.matches('[data-demo-form], [data-shopify-contact]')) {
        if (!f.matches('[data-shopify-contact]')) e.preventDefault();
        let ok = true;
        $$('[required]', f).forEach(inp => {
          const field = inp.closest('.field'); const valid = inp.type === 'email' ? /^\S+@\S+\.\S+$/.test(inp.value) : inp.value.trim().length > 1;
          field?.classList.toggle('is-invalid', !valid); if (!valid && ok) { inp.focus(); ok = false; }
        });
        if (!ok) { e.preventDefault(); return; }
        const btn = $('[type="submit"]', f); btn?.classList.add('is-loading');
        // Shopify contact forms keep the same validation, then submit to Shopify normally.
        if (f.matches('[data-shopify-contact]')) return;
        setTimeout(() => { (f.closest('[data-form-wrap]') || f).classList.add('is-sent'); btn?.classList.remove('is-loading'); }, 700);
      }
    });
    document.addEventListener('input', e => { const fld = e.target.closest('.field.is-invalid'); if (fld) fld.classList.remove('is-invalid'); });
    // check a filled-in field as you leave it (an empty one waits for submit, so tabbing past isn't scolded)
    document.addEventListener('focusout', e => {
      const inp = e.target;
      // tidy an Irish number the way it's said: 0871234567 → 087 123 4567, 00353… → +353 87 123 4567
      if (inp.matches?.('input[type="tel"]')) {
        const d = inp.value.replace(/[^\d+]/g, ''); let m;
        if ((m = d.match(/^01(\d{3})(\d{4})$/))) inp.value = `01 ${m[1]} ${m[2]}`;
        else if ((m = d.match(/^0(\d{2})(\d{3})(\d{4})$/))) inp.value = `0${m[1]} ${m[2]} ${m[3]}`;
        else if ((m = d.match(/^(?:\+|00)353(\d{2})(\d{3})(\d{4})$/))) inp.value = `+353 ${m[1]} ${m[2]} ${m[3]}`;
      }
      if (!inp.matches?.('.input[required], .textarea[required]')) return;
      const fld = inp.closest('.field'), v = inp.value.trim(); if (!fld || !v) return;
      fld.classList.toggle('is-invalid', inp.type === 'email' ? !/^\S+@\S+\.\S+$/.test(v) : v.length < 2);
    });
  }

  /* ---------- smooth scroll ---------- */
  function initLenis() {
    if (reduced || !window.Lenis || !fine) return;
    let against = 0, againstN = 0, againstAt = 0, lastDir = 0, lastAt = -1e9, undo = null;
    lenis = new window.Lenis({
      duration: 1.15, easing: t => Math.min(1, 1.001 - Math.pow(2, -10 * t)), smoothWheel: true,
      // A wheel turned the other way turns the page at once. Each tick adds to the glide's target,
      // so after scrolling one way the target sits well ahead of the page, and the first ticks the
      // other way only used that up: the page kept going, stalled, then came back, a hitch felt as
      // resistance at every change of direction. Now the glide still owed is dropped first.
      // But only for a real turn, as the wheel may say the other way when the hand hasn't:
      // - a worn or dusty wheel bounces: in the middle of a roll it gives a whole notch the other way
      //   25-40 ms before or after one the right way (seen on a scroll log, 23 Oct), far quicker than
      //   a hand turns a wheel round. So of two notches opposite ways within 70 ms, one is let go: the
      //   second, unless the first was itself a change of way, which the second then undoes
      // - small ticks the other way (a wheel's flick as a notch settles, or a trackpad) turn it on
      //   one of more than 24px, or a second in a row once they add up to 8px (a trackpad's turn: a
      //   run of small ones); a lone flick, or two of a pixel or so, is let go
      // What's let go is kept from scrolling the page natively, which Lenis would no longer prevent
      // While a drawer, menu, search or dialog locks the page, the wheel is the browser's: stopped,
      // Lenis would cancel it, and the cart's list and the other panels couldn't scroll (the page
      // can't move then anyway, its overflow is hidden)
      prevent: () => locks > 0,
      virtualScroll: ({ deltaY, event }) => {
        if (!deltaY || locks) return true;
        const now = performance.now(), dir = Math.sign(deltaY), notch = Math.abs(deltaY) >= 50;
        const drop = () => { if (event.cancelable) event.preventDefault(); return false; };
        if (notch) {
          if (dir !== lastDir && now - lastAt < 70) {
            if (!undo || now - undo.at >= 70) return drop();
            lenis.targetScroll = undo.target; undo = null; lastDir = dir; lastAt = now;
            return true;
          }
          undo = dir !== lastDir ? { target: lenis.targetScroll, at: now } : null;
          lastDir = dir; lastAt = now;
        }
        const owed = lenis.targetScroll - lenis.animatedScroll;
        if (Math.abs(owed) < 1 || dir === Math.sign(owed)) { against = 0; againstN = 0; return true; }
        if (!notch) {
          if (now - againstAt > 150) { against = 0; againstN = 0; }
          against += Math.abs(deltaY); againstN++; againstAt = now;
          if (against <= 24 && (againstN < 2 || against < 8)) return drop();
        }
        lenis.targetScroll = lenis.animatedScroll; against = 0; againstN = 0;
        return true;
      },
    });
    const raf = t => { lenis.raf(t); requestAnimationFrame(raf); };
    requestAnimationFrame(raf);
    A.lenis = lenis;
    document.addEventListener('click', e => {
      // smooth-scroll any link to an anchor on this same page ("#quote" or "contact.html#quote" while on contact.html)
      const a = e.target.closest('a[href*="#"]'); if (!a) return;
      const url = new URL(a.href, location.href);
      if (url.pathname !== location.pathname || url.hash.length < 2) return;
      const t = document.querySelector(url.hash); if (!t) return;
      e.preventDefault();
      const menu = $('#mnav')?.classList.contains('is-open');
      if (menu) $('[data-mnav-close]')?.click();
      // (Lenis keeps the page's scroll-padding-top and the target's scroll-margin-top, as the browser does on touch;
      // from the mobile menu, once it has closed and given the page its scroll back)
      setTimeout(() => { const fit = fitTop(t); lenis.scrollTo(fit ?? t); }, menu ? 420 : 0);
      history.replaceState(null, '', url.hash);
    });
  }

  // A section marked [data-fit] (the home page's app section) is framed on the screen when a link
  // brings you to it: its [data-fit] box centred in the room under the header. Where the box is
  // taller than that (a phone), its core, the parts marked [data-fit-core], is framed instead, or
  // failing that its top goes just under the header. Null for any other target
  function fitTop(target) {
    const box = target.matches('[data-fit]') ? target : target.querySelector('[data-fit]'); if (!box) return null;
    // (the header's pill, as it sits once the page has scrolled: its height, its 12px from the top, and a little air)
    const bar = $('.nav-bar'), head = bar ? bar.offsetHeight + 20 : 0, room = innerHeight - head;
    // (where things are laid out, not where a reveal has them sliding in from)
    const docTop = el => { let t = 0; for (; el; el = el.offsetParent) t += el.offsetTop; return t; };
    let top = docTop(box), h = box.offsetHeight;
    const core = h > room ? $$('[data-fit-core]', box) : [];
    if (core.length) { const tops = core.map(docTop); h = Math.max(...core.map((c, i) => tops[i] + c.offsetHeight)) - (top = Math.min(...tops)); }
    return Math.max(0, top - head - Math.max(0, (room - h) / 2));
  }

  /* ---------- preloader (home, once per session) ---------- */
  function preloader() {
    const pl = $('.preloader'); if (!pl) return Promise.resolve();
    // (index.html's head has already hidden it, before the first paint, if it has been seen this visit)
    let seen = root.classList.contains('intro-seen'); try { seen = seen || sessionStorage.getItem('aura-intro'); sessionStorage.setItem('aura-intro', 1); } catch {}
    if (seen || reduced) { pl.remove(); return Promise.resolve(); }
    // it lifts once its own drawing has played (1.95s from when it first showed, which on a slow
    // connection has often been and gone by the time the page is ready) and the photo it uncovers
    // is in, waiting no more than 2.5s for that
    const played = Math.max(0, ...pl.getAnimations({ subtree: true }).map(a => a.currentTime || 0));
    const photo = $('.hs__scene img');
    const inPhoto = photo && !photo.complete ? Promise.race([photo.decode().catch(() => {}), new Promise(r => setTimeout(r, 2500))]) : null;
    return Promise.all([new Promise(r => setTimeout(r, Math.max(0, 1950 - played))), inPhoto])
      .then(() => { pl.classList.add('is-done'); setTimeout(() => { pl.remove(); }, 1100); });
  }

  /* ---------- public API ---------- */
  const readyFns = [];
  const A = window.A = { $, $$, esc, fmt, icon, stars, logo, qs, seo, page, route, asset: D.asset, html: D.html, byHandle: D.byHandle, Cart, productCard, optCard, swatchesHTML, swatchPeek, cardColors, fitTop, toast, hydrateButtons, hydrateIcons, enhanceSelects, reviewCard, reviewRows, faqHTML, jcard, initAccordions, splitLines, initials, avColor, centerInRail, cartLinePriceHTML, cartLineDealHTML, volumeNoteHTML, recent, markViewed, payIcon, reduced, fine, lock, unlock, ready: fn => readyFns.push(fn) };

  document.addEventListener('DOMContentLoaded', () => {
    $$('[data-feature]').forEach(el => { if (!D.features[el.dataset.feature]) el.remove(); });
    if (!document.getElementById('siteHeader')) mountTop();
    mountFoot();
    initFootCols(); initFootDusk(); initCopy(); initCart(); initQuick(); initForms(); initLenis(); initPress();
    readyFns.forEach(fn => { try { fn(A); } catch (err) { console.error(err); } });
    $$('main [data-fills]').forEach(el => el.classList.add('is-filled'));   // includes Shopify's section wrappers
    seo();
    // the page complete (icons, buttons, selects) before anything below measures it, so it's laid out
    // once as it opens rather than once for them and again after
    hydrateIcons(); hydrateButtons(); initAccordions(); enhanceSelects();
    initFab(); initRails(); initFootFit();   // after the page scripts, so they see what those rendered
    // a product card's colour dots show that finish's photo too, wherever cards are
    swatchPeek(document, (name, dot) => D.byHandle(dot.closest('[data-handle]')?.dataset.handle)?.colors?.find(c => c.name === name)?.image, '#EEF1F4', '.pcard__swatches [data-color]');
    initRail();
    const go = () => {
      root.classList.add('is-loaded'); initMotion();
      // arriving by a link to a framed section (index.html#app from another page): framed, and kept
      // framed while what's above it is still coming in and taking its room, and after the browser's
      // own jump to the anchor once the page has loaded, until you scroll yourself or 4s have passed
      const at = location.hash.length > 1 && document.querySelector(location.hash);
      // (the window itself, not Lenis: after the browser's jump Lenis still thinks it's where it was,
      // and would take a scroll back there for one it has already done)
      const frame = () => { const fit = fitTop(at); if (fit != null && Math.abs(scrollY - fit) > 2) { scrollTo(0, fit); lenis?.scrollTo(fit, { immediate: true, force: true }); } };
      if (at && fitTop(at) != null) {
        frame();
        const ro = new ResizeObserver(() => requestAnimationFrame(frame)); ro.observe(document.body);
        const moved = () => requestAnimationFrame(frame);
        addEventListener('scroll', moved, { passive: true });
        const stop = () => { ro.disconnect(); removeEventListener('scroll', moved); ['wheel', 'touchstart', 'keydown', 'pointerdown'].forEach(t => removeEventListener(t, stop)); };
        ['wheel', 'touchstart', 'keydown', 'pointerdown'].forEach(t => addEventListener(t, stop, { passive: true }));
        setTimeout(stop, 4000);
      }
      const shot = qs('shot'); // QA: ?shot=<css selector> jumps straight to a section
      if (shot) { const el = document.querySelector(shot); if (el) { const y = el.getBoundingClientRect().top + scrollY - (+qs('off') || 100); lenis ? lenis.scrollTo(y, { immediate: true, force: true }) : scrollTo(0, y); dispatchEvent(new Event('scroll')); } }
    };
    // A page prerendered by instant navigation holds its entrance motion until it is actually shown
    const shown = () => (document.prerendering ? new Promise(r => document.addEventListener('prerenderingchange', r, { once: true })) : Promise.resolve());
    // the faces the split headings and the body text are set in, as soon as they're in: they're measured
    // into lines as the page enters. (document.fonts.ready settles only at the next layout after the
    // last font, and waits for any font the page asks for later.) A face that fails to load doesn't hold it up
    const fontsIn = () => {
      if (!document.fonts) return;
      const fonts = new Set([document.body, ...$$('[data-split], [data-split] *')].map(el => { const s = getComputedStyle(el); return `${s.fontStyle} ${s.fontWeight} 1em ${s.fontFamily}`; }));
      return Promise.all([...fonts].map(f => document.fonts.load(f).catch(() => {})));
    };
    // never let slow web fonts hold the page hostage: proceed after 1.2s regardless
    shown().then(preloader).then(() => Promise.race([fontsIn(), new Promise(r => setTimeout(r, 1200))])).then(go);
  });
})();
